import { useState, useEffect, useRef } from 'react'
import { Map, View } from 'ol'
import TileLayer from 'ol/layer/Tile'
import OSM from 'ol/source/OSM'
import ImageWMS from 'ol/source/ImageWMS'
import TileWMS from 'ol/source/TileWMS'
import ImageLayer from 'ol/layer/Image'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { fromLonLat, toLonLat } from 'ol/proj'
import { Feature } from 'ol'
import { Point, LineString } from 'ol/geom'
import { Style, Icon, Stroke } from 'ol/style'
import Overlay from 'ol/Overlay'
import { ScaleLine } from 'ol/control'
import { Modify } from 'ol/interaction'
import 'ol/ol.css'
import MapLayout from './MapLayout'
import SegmentPropertiesModal from './SegmentPropertiesModal'
import MeasurementPointModal from './MeasurementPointModal'
import ValveCreationModal from './ValveCreationModal'
import ValveStatusChangeModal from './ValveStatusChangeModal'
import HydrantCreationModal from './HydrantCreationModal'
import HydrantEditModal, { HydrantData } from './HydrantEditModal'
import HydrantImportModal from './HydrantImportModal'
import ControllersModal from './ControllersModal'
import MeasurementHistoryModal from './MeasurementHistoryModal'
import UserLocationMarker from './UserLocationMarker'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useDeviceLocation } from '../../hooks/useDeviceLocation'

interface InfrastructureObject {
  id: number
  name: string
  latitude: string
  longitude: string
  client_name: string
  address: string
  description: string
  infrastructure_type: string
}

// Default center coordinates (Piaseczno area)
const DEFAULT_CENTER: [number, number] = [20.835743, 51.978431] // [lng, lat] for OpenLayers
const DEFAULT_ZOOM = 12

// Function to get infrastructure icon based on type using Lucide icons
const getInfrastructureIcon = (type: string | undefined | null): string => {
  if (!type) return getDefaultIcon()

  const normalizedType = type.toLowerCase().trim()

  if (normalizedType.includes('stacja') && normalizedType.includes('uzdatniania') && normalizedType.includes('wody')) {
    // Water treatment plant - Droplets icon
    return getLucideIcon('droplets', '#0066CC')
  } else if (normalizedType.includes('oczyszczalnia') && normalizedType.includes('ścieków')) {
    // Wastewater treatment plant - Filter icon
    return getLucideIcon('filter', '#8B4513')
  } else if (normalizedType.includes('przepompownia') && normalizedType.includes('ścieków')) {
    // Sewage pumping station - CircleDot icon
    return getLucideIcon('circle-dot', '#333333')
  } else {
    // Default marker for other types - MapPin icon
    return getLucideIcon('map-pin', '#3B82F6')
  }
}

// Function to create SVG from Lucide icon paths
const getLucideIcon = (iconName: string, color: string): string => {
  const iconPaths: Record<string, string> = {
    'droplets': 'M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z m5.56-9.7A10.13 10.13 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.48 2 1.57 3 3.05 3 5.22 0 3.39-2.69 6.13-6 6.13s-6-2.74-6-6.13c0-2.18 1-4.66 2.44-6.6z',
    'filter': 'M22 3H2l8 9.46V19l4 2v-8.54L22 3z',
    'circle-dot': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    'map-pin': 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z M12 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4z'
  }

  const path = iconPaths[iconName] || iconPaths['map-pin']

  return `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="${path}" fill="${color}" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `
}

const getDefaultIcon = (): string => getLucideIcon('map-pin', '#3B82F6')

interface MapPageProps {
  token: string
  onLogout: () => void
}

export default function MapPage({ token, onLogout: _onLogout }: MapPageProps) {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstance = useRef<Map | null>(null)
  const clickOverlay = useRef<Overlay | null>(null)
  const wmsErrorToastCooldownRef = useRef<Record<string, number>>({})
  const wmsUnavailableRef = useRef<Record<string, boolean>>({})
  const handleToggleValveStatusRef = useRef<(valve: any) => void>(() => {})
  const handleHydrantClickRef = useRef<(hydrant: any) => void>(() => {})
  const { showSuccess, showError, showInfo, showConfirm } = useNotificationContext()

  // Note: imageCache is kept for potential future use with WMS label optimization

  // === Helper: load persisted layer states from localStorage ===
  const loadLayerState = (key: string, defaultValue: boolean): boolean => {
    try {
      const saved = localStorage.getItem(`map_layer_${key}`)
      if (saved !== null) return saved === 'true'
    } catch (e) { /* ignore */ }
    return defaultValue
  }

  const saveLayerState = (key: string, value: boolean) => {
    try {
      localStorage.setItem(`map_layer_${key}`, value.toString())
    } catch (e) { /* ignore */ }
  }

  // === Helper: load/save cached data from sessionStorage ===
  const loadCachedData = <T,>(key: string): T | null => {
    try {
      const cached = sessionStorage.getItem(`map_data_${key}`)
      if (cached) return JSON.parse(cached) as T
    } catch (e) { /* ignore */ }
    return null
  }

  const saveCachedData = (key: string, data: any) => {
    try {
      sessionStorage.setItem(`map_data_${key}`, JSON.stringify(data))
    } catch (e) { /* ignore - sessionStorage may be full */ }
  }

  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER)
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showInfrastructure, setShowInfrastructure] = useState(() => loadLayerState('infrastructure', false))
  const [infrastructureData, setInfrastructureData] = useState<InfrastructureObject[]>(() => loadCachedData<InfrastructureObject[]>('infrastructure') || [])
  const [, setLoadingInfrastructure] = useState(false)

  // Powiatowe data layers
  const [showDzialki, setShowDzialki] = useState(() => loadLayerState('dzialki', false))
  const [showBudynki, setShowBudynki] = useState(() => loadLayerState('budynki', false))
  const [showRejestrWlasnosci, setShowRejestrWlasnosci] = useState(() => loadLayerState('rejestr_wlasnosci', false))
  // Sieci uzbrojenia terenu (mapa zasadnicza WMS)
  const [showGaz, setShowGaz] = useState(() => loadLayerState('gaz', false))
  const [showTeletechnika, setShowTeletechnika] = useState(() => loadLayerState('teletechnika', false))
  const [showElektryka, setShowElektryka] = useState(() => loadLayerState('elektryka', false))
  // Warstwa "Przepływy" (telemetria z przepływomierzy) + zarządzanie kontrolerami
  const [showFlows, setShowFlows] = useState(() => loadLayerState('flows', false))
  const [flowsData, setFlowsData] = useState<any[]>([])
  const [showControllersModal, setShowControllersModal] = useState(false)
  const [historyPoint, setHistoryPoint] = useState<{ id: number; name: string } | null>(null)
  const handleFlowClickRef = useRef<(p: any) => void>(() => {})
  // Zarządzanie punktem pomiarowym: przenoszenie na mapie + edycja danych
  const [movingPoint, setMovingPoint] = useState<{ id: number; name: string } | null>(null)
  const movingPointRef = useRef<{ id: number; name: string } | null>(null)
  const [editPoint, setEditPoint] = useState<any | null>(null)
  // Tryb "informacje o obiekcie" (WMS GetFeatureInfo po kliknięciu) - domyślnie WYŁĄCZONY
  const [featureInfoMode, setFeatureInfoMode] = useState(false)
  const [featureInfoData, setFeatureInfoData] = useState<{ html?: string; coord: [number, number]; loading?: boolean; error?: string } | null>(null)
  const [showWodociagi, setShowWodociagi] = useState(() => loadLayerState('wodociagi', false))
  const [showKanalizacja, setShowKanalizacja] = useState(() => loadLayerState('kanalizacja', false))
  const [showGraniceMiejscowosci, setShowGraniceMiejscowosci] = useState(() => loadLayerState('granice_miejscowosci', false))

  // Tryb edycji
  const [editMode, setEditMode] = useState(false)
  const [showWaterSegments, setShowWaterSegments] = useState(() => loadLayerState('water_segments', false))
  const [waterSegmentsData, setWaterSegmentsData] = useState<any[]>(() => loadCachedData<any[]>('water_segments') || [])
  const [, setLoadingWaterSegments] = useState(false)

  // Sewer segments
  const [showSewerSegments, setShowSewerSegments] = useState(() => loadLayerState('sewer_segments', false))
  const [sewerSegmentsData, setSewerSegmentsData] = useState<any[]>(() => loadCachedData<any[]>('sewer_segments') || [])
  const [, setLoadingSewerSegments] = useState(false)

  // Segment type selection
  const [segmentType, setSegmentType] = useState<'water' | 'sewer'>('water')

  // Tryb rysowania linii
  const [drawingMode, setDrawingMode] = useState(false)
  const [drawingPoints, setDrawingPoints] = useState<[number, number][]>([])
  const [showDrawingControls, setShowDrawingControls] = useState(false)
  const [drawingLayer, setDrawingLayer] = useState<any>(null)

  // Tryb pomiaru odległości
  const [measurementMode, setMeasurementMode] = useState(false)
  const [measurementPoints, setMeasurementPoints] = useState<[number, number][]>([])

  // Tryb edycji odcinka
  const [editingSegment, setEditingSegment] = useState<any>(null)
  const [editingMode, setEditingMode] = useState(false)

  // Punkty pomiarowe
  const [showMeasurementPoints, setShowMeasurementPoints] = useState(() => loadLayerState('measurement_points', false))
  const [creatingPointMode, setCreatingPointMode] = useState(false)

  // Realizations layer
  const [showRealizations, setShowRealizations] = useState(() => loadLayerState('realizations', false))
  const [realizationsData, setRealizationsData] = useState<any[]>(() => loadCachedData<any[]>('realizations') || [])
  const [, setLoadingRealizations] = useState(false)

  // Modal właściwości odcinka
  const [showSegmentModal, setShowSegmentModal] = useState(false)
  const [suwOptions, setSuwOptions] = useState<any[]>([])
  const [segmentOptions, setSegmentOptions] = useState<any[]>([])

  // Modal punktów pomiarowych
  const [showPointModal, setShowPointModal] = useState(false)
  const [selectedSegmentForPoint, setSelectedSegmentForPoint] = useState<any>(null)
  const [newPointCoordinates, setNewPointCoordinates] = useState<[number, number] | null>(null)

  // Lokalizacja urządzenia
  const deviceLocation = useDeviceLocation()
  const [showUserLocation, setShowUserLocation] = useState(() => loadLayerState('user_location', false))
  const [followMode, setFollowMode] = useState(false)

  // Network valves (zasuwy sieciowe)
  const [showNetworkValves, setShowNetworkValves] = useState(() => loadLayerState('network_valves', false))
  const [networkValvesData, setNetworkValvesData] = useState<any[]>(() => loadCachedData<any[]>('network_valves') || [])
  const [, setLoadingNetworkValves] = useState(false)
  const [placingValveMode, setPlacingValveMode] = useState(false)
  const [showValveModal, setShowValveModal] = useState(false)
  const [newValveCoordinates, setNewValveCoordinates] = useState<[number, number] | null>(null)
  const [valveForStatusChange, setValveForStatusChange] = useState<any>(null)

  // Network hydrants (hydranty)
  const [showNetworkHydrants, setShowNetworkHydrants] = useState(() => loadLayerState('network_hydrants', false))
  const [networkHydrantsData, setNetworkHydrantsData] = useState<any[]>(() => loadCachedData<any[]>('network_hydrants') || [])
  const [placingHydrantMode, setPlacingHydrantMode] = useState(false)
  const [showHydrantModal, setShowHydrantModal] = useState(false)
  const [newHydrantCoordinates, setNewHydrantCoordinates] = useState<[number, number] | null>(null)
  const [hydrantForEdit, setHydrantForEdit] = useState<HydrantData | null>(null)
  const [showHydrantImport, setShowHydrantImport] = useState(false)

  // Podkład: ortofotomapa (GUGiK) - domyślnie wyłączona
  const [showOrtofoto, setShowOrtofoto] = useState(() => loadLayerState('ortofoto', false))
  const orthoLayerRef = useRef<any>(null)
  const handleToggleOrtofoto = () => setShowOrtofoto(v => !v)

  // Aktywny referat użytkownika (do przypisywania hydrantów/zasuw)
  const [mapClientId, setMapClientId] = useState<number | null>(null)

  // Wybrany segment w trybie przeglądania
  const [selectedSegment, setSelectedSegment] = useState<any>(null)
  const [showSelectedSegmentTooltip, setShowSelectedSegmentTooltip] = useState(false)

  const formatDistance = (meters: number): string => {
    if (meters < 1000) return `${meters.toFixed(1)} m`
    return `${(meters / 1000).toFixed(3)} km`
  }

  const haversineDistanceMeters = (a: [number, number], b: [number, number]): number => {
    const toRad = (deg: number) => (deg * Math.PI) / 180
    const R = 6371000
    const [lon1, lat1] = a
    const [lon2, lat2] = b
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const lat1Rad = toRad(lat1)
    const lat2Rad = toRad(lat2)

    const h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(dLon / 2) * Math.sin(dLon / 2)

    return 2 * R * Math.asin(Math.sqrt(h))
  }

  const measurementSegments = measurementPoints.slice(1).map((point, idx) => {
    const from = measurementPoints[idx]
    const distance = haversineDistanceMeters(from, point)
    return {
      index: idx + 1,
      distance
    }
  })

  const measurementTotal = measurementSegments.reduce((sum, seg) => sum + seg.distance, 0)

  const handleWmsLoadError = (layerId: string, serviceUrl: string) => {
    const now = Date.now()
    const last = wmsErrorToastCooldownRef.current[layerId] || 0
    const cooldownMs = 30000

    if (now - last > cooldownMs) {
      wmsErrorToastCooldownRef.current[layerId] = now
      showError(
        'Warstwa WMS niedostępna',
        `Nie udało się pobrać warstwy (${layerId}) z ${serviceUrl}. Sprawdź połączenie lub dostępność serwisu powiatu.`
      )
    }

    wmsUnavailableRef.current[layerId] = true
  }

  const handleWmsLoadSuccess = (layerId: string) => {
    if (wmsUnavailableRef.current[layerId]) {
      showSuccess('Warstwa WMS wróciła', `Warstwa ${layerId} ponownie odpowiada.`)
      wmsUnavailableRef.current[layerId] = false
    }
  }

  // Load saved map position from localStorage
  useEffect(() => {
    const savedCenter = localStorage.getItem('mapCenter')
    const savedZoom = localStorage.getItem('mapZoom')

    if (savedCenter) {
      try {
        const center = JSON.parse(savedCenter)
        setMapCenter(center)
      } catch (e) {
        console.warn('Invalid saved map center')
      }
    }

    if (savedZoom) {
      try {
        const zoom = parseInt(savedZoom)
        setMapZoom(zoom)
      } catch (e) {
        console.warn('Invalid saved map zoom')
      }
    }
  }, [])

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return

    // Create base layers - use OSM with higher zoom support
    const osmLayer = new TileLayer({
      source: new OSM({
        maxZoom: 19  // OSM serwuje kafle do z19; OL rozciąga je dla wyższych zoomów.
                     // maxZoom 22 powodował żądania nieistniejących kafli -> błędy CORS w konsoli.
      })
    })

    // Ortofotomapa (GUGiK) - podkład rastrowy, domyślnie wyłączony.
    // Kafle WMS w EPSG:3857; ładowane jako <img> (bez crossOrigin => bez wymogu CORS).
    const orthoLayer = new TileLayer({
      visible: showOrtofoto,
      source: new TileWMS({
        url: 'https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMS/StandardResolution',
        params: {
          'LAYERS': 'Raster',
          'FORMAT': 'image/jpeg',
          'VERSION': '1.3.0'
        },
        projection: 'EPSG:3857',
        attributions: '© GUGiK - ortofotomapa'
      })
    })
    ;(orthoLayer as any).set('id', 'ortofoto')
    orthoLayerRef.current = orthoLayer

    // Create Tarczyn boundaries layer
    const tarczynLayer = new ImageLayer({
      source: new ImageWMS({
        url: 'https://granice.e-mapa.net/cgi-bin/granice',
        params: {
          'LAYERS': 'gminy',
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'VERSION': '1.1.1',
          'ter': '141806%'
        },
        serverType: 'geoserver'
      }),
      opacity: 0.7
    })

    // Create map
    const map = new Map({
      target: mapRef.current,
      layers: [osmLayer, orthoLayer, tarczynLayer],
      view: new View({
        center: fromLonLat(mapCenter),
        zoom: mapZoom,
        maxZoom: 22,
        minZoom: 1
      }),
      controls: [
        new ScaleLine({
          units: 'metric',
          bar: true,
          steps: 4,
          text: true,
          minWidth: 140
        })
      ]
    })

    // Create click overlay for coordinates display
    const overlayElement = document.createElement('div')
    overlayElement.className = 'bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-2 text-sm'
    overlayElement.innerHTML = '<div class="font-medium text-gray-900">Kliknięta lokalizacja</div><div class="text-gray-600"></div>'

    const overlay = new Overlay({
      element: overlayElement,
      positioning: 'bottom-center',
      stopEvent: false,
      offset: [0, -10]
    })
    map.addOverlay(overlay)
    clickOverlay.current = overlay

    mapInstance.current = map

    // Auto-skalowanie: przerysuj mapę, gdy zmieni się rozmiar kontenera
    // (obrót urządzenia, schowanie sidebara, pasek przeglądarki mobilnej, pełny ekran).
    const scheduleUpdateSize = () => {
      // requestAnimationFrame, aby updateSize zadziałał po przeliczeniu layoutu
      requestAnimationFrame(() => mapInstance.current?.updateSize())
    }

    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined' && mapRef.current) {
      resizeObserver = new ResizeObserver(scheduleUpdateSize)
      resizeObserver.observe(mapRef.current)
    }
    window.addEventListener('resize', scheduleUpdateSize)
    window.addEventListener('orientationchange', scheduleUpdateSize)

    return () => {
      window.removeEventListener('resize', scheduleUpdateSize)
      window.removeEventListener('orientationchange', scheduleUpdateSize)
      if (resizeObserver) resizeObserver.disconnect()
      if (mapInstance.current) {
        mapInstance.current.setTarget(undefined)
        mapInstance.current = null
      }
    }
  }, [])

  // Handle map clicks - separate effect to react to drawingMode changes
  useEffect(() => {
    if (!mapInstance.current) return

    const map = mapInstance.current

    // Add new click handler
    const clickHandler = (event: any) => {
      // Przenoszenie punktu pomiarowego ma najwyższy priorytet
      if (movingPointRef.current) {
        handleMapClickForPointMove(event)
        return
      }

      // Tryb "informacje o obiekcie" ma pierwszeństwo, gdy aktywny
      if (featureInfoMode) {
        handleFeatureInfoClick(event)
        return
      }

      // Handle measurement mode first
      if (measurementMode) {
        const coordinates = toLonLat(event.coordinate)
        setMeasurementPoints(prev => [...prev, [coordinates[0], coordinates[1]]])
        return
      }

      // Handle valve placement mode
      if (placingValveMode) {
        handleMapClickForValvePlacement(event)
        return
      }

      // Handle hydrant placement mode
      if (placingHydrantMode) {
        handleMapClickForHydrantPlacement(event)
        return
      }

      // Handle point creation mode first
      if (creatingPointMode) {
        handleMapClickForPointCreation(event)
        return
      }

      // Handle editing mode first - select segment to edit
      if (editingMode) {
        handleMapClickForEditing(event)
        return
      }

      // Handle drawing mode first
      if (drawingMode) {
        handleMapClickForDrawing(event)
        return
      }

      // In edit mode, don't show coordinates
      if (editMode) {
        return
      }

      // Normal mode - check for local layer features
      const map = mapInstance.current
      if (!map) return

      // Network valves click is handled by dedicated 'click' handler in valves layer useEffect

      // Check if any local layers are enabled and check for features
      const localLayersEnabled = showInfrastructure || showWaterSegments || showMeasurementPoints
      let clickedOnFeature = false
      let featureData: any = null

      if (localLayersEnabled) {
        const pixel = event.pixel || map.getEventPixel(event.originalEvent)

        // Check infrastructure layer
        if (showInfrastructure) {
          const infraFeatures = map.getFeaturesAtPixel(pixel, {
            layerFilter: (layer) => (layer as any).get('id') === 'infrastructure'
          })
          if (infraFeatures.length > 0) {
            clickedOnFeature = true
            const feature = infraFeatures[0] as Feature
            featureData = {
              type: 'infrastructure',
              data: feature.get('infrastructureData')
            }
          }
        }

        // Check water segments layer
        if (showWaterSegments && !clickedOnFeature) {
          const segmentFeatures = map.getFeaturesAtPixel(pixel, {
            layerFilter: (layer) => (layer as any).get('id') === 'water-segments'
          })
          if (segmentFeatures.length > 0) {
            clickedOnFeature = true
            const feature = segmentFeatures[0] as Feature
            const segmentData = feature.get('waterSegmentData')
            const isLine = feature.get('isLine')

            // Only select water segment lines, not points
            if (isLine && segmentData) {
              // Set selected segment and show tooltip instead of coordinates
              setSelectedSegment(segmentData)
              setShowSelectedSegmentTooltip(true)

              // Don't show the regular click coordinates overlay
              return
            } else {
              featureData = {
                type: 'water-segment-point',
                data: segmentData,
                isLine: isLine,
                isStartPoint: feature.get('isStartPoint'),
                isEndPoint: feature.get('isEndPoint')
              }
            }
          }
        }

        // Check measurement points layer - disabled for now as layer doesn't exist yet
        // if (showMeasurementPoints && !clickedOnFeature) {
        //   const pointFeatures = map.getFeaturesAtPixel(pixel, {
        //     layerFilter: (layer) => (layer as any).get('id') === 'measurement-points'
        //   })
        //   if (pointFeatures.length > 0) {
        //     clickedOnFeature = true
        //     const feature = pointFeatures[0] as Feature
        //     featureData = {
        //       type: 'measurement-point',
        //       data: feature.get('measurementPointData')
        //     }
        //   }
        // }
      }

      // Show overlay only if clicked on a local layer feature
      if (clickedOnFeature && featureData) {
        // In future: show detailed information overlay here
        const element = clickOverlay.current?.getElement()
        if (element) {
          const coordDiv = element.querySelector('div:last-child')
          if (coordDiv) {
            // For now, keep simple coordinates display - can be enhanced later
            const coordinates = toLonLat(event.coordinate)
            coordDiv.textContent = `${coordinates[1].toFixed(6)}, ${coordinates[0].toFixed(6)}`
          }
          clickOverlay.current?.setPosition(event.coordinate)
        }
      } else {
        // Hide overlay if clicking on empty space

        // Reset selected segment if clicking on empty space
        setSelectedSegment(null)
        setShowSelectedSegmentTooltip(false)
        clickOverlay.current?.setPosition(undefined)
      }
    }

    map.on('singleclick', clickHandler)

    // Cleanup function
    return () => {
      map.un('singleclick', clickHandler)
    }
  }, [measurementMode, drawingMode, editMode, editingMode, creatingPointMode, placingValveMode, placingHydrantMode, featureInfoMode, showWodociagi, showKanalizacja, showGaz, showTeletechnika, showElektryka, showInfrastructure, showWaterSegments, showMeasurementPoints, showNetworkValves, showNetworkHydrants])

  const handleToggleMeasurementMode = () => {
    const next = !measurementMode
    setMeasurementMode(next)

    if (mapInstance.current) {
      const target = mapInstance.current.getTarget()
      if (target && typeof target === 'object' && 'style' in target) {
        ;(target as HTMLElement).style.cursor = next ? 'crosshair' : ''
      }
    }

    if (next) {
      showInfo('Tryb pomiaru', 'Klikaj kolejne punkty na mapie, aby zmierzyć odległość odcinków i sumę.')
    }
  }

  const handleClearMeasurement = () => {
    setMeasurementPoints([])
  }

  // Handle map move events
  useEffect(() => {
    if (!mapInstance.current) return

    const map = mapInstance.current

    const moveHandler = () => {
      const view = map.getView()
      const center = view.getCenter()
      const zoom = view.getZoom()
      if (center && zoom !== undefined) {
        const lonLat = toLonLat(center)
        setMapCenter([lonLat[0], lonLat[1]])
        setMapZoom(zoom)
        localStorage.setItem('mapCenter', JSON.stringify([lonLat[0], lonLat[1]]))
        localStorage.setItem('mapZoom', zoom.toString())
      }
    }

    map.getView().on('change:center', moveHandler)

    return () => {
      map.getView().un('change:center', moveHandler)
    }
  }, [])

  // Update WMS layers when toggles change
  useEffect(() => {
    if (!mapInstance.current) return

    const map = mapInstance.current
    const layers = map.getLayers().getArray()

    // Remove existing WMS layers (keep OSM and Tarczyn)
    const layersToRemove = layers.filter(layer => {
      const layerId = (layer as any).get('id')
      return layerId && layerId.startsWith('wms-')
    })
    layersToRemove.forEach(layer => map.removeLayer(layer))

    // Add powiatowe layers
    if (showRejestrWlasnosci) {
      // Mapa własności EGiB - grupy rejestrowe (ePODGiK, ta sama usługa co działki).
      // Nazwa warstwy 'grupy' potwierdzona w GetCapabilities usługi.
      // Dodawana jako PIERWSZA z warstw WMS: leży nad mapą podkładową,
      // ale pod wszystkimi pozostałymi aktywnymi warstwami.
      const rejestrUrl = 'https://wms.epodgik.pl/cgi-bin/piaseczno'
      const rejestrSource = new ImageWMS({
        url: rejestrUrl,
        params: {
          'LAYERS': 'grupy',
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'VERSION': '1.1.1'
        },
        serverType: 'geoserver'
      })
      rejestrSource.on('imageloaderror', () => handleWmsLoadError('wms-rejestr-wlasnosci', rejestrUrl))
      rejestrSource.on('imageloadend', () => handleWmsLoadSuccess('wms-rejestr-wlasnosci'))

      const rejestrLayer = new ImageLayer({
        source: rejestrSource,
        opacity: 0.45 // duża przezroczystość, by nie przysłaniać podkładu i innych warstw
      })
      ;(rejestrLayer as any).set('id', 'wms-rejestr-wlasnosci')
      map.addLayer(rejestrLayer)
    }

    if (showDzialki) {
      // Używamy dedykowanego serwisu WMS dla działek z Twojego linku
      const dzialkiUrl = 'https://wms.epodgik.pl/cgi-bin/piaseczno'
      const dzialkiSource = new ImageWMS({
        url: dzialkiUrl, // Zmieniony URL na piaseczno (bez -mz)
        params: {
          'LAYERS': 'numery_dzialek,dzialki', // Dedykowane warstwy dla działek
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'VERSION': '1.1.1',
          'STYLES': ',,' // Puste style jak w Twoim linku
        },
        serverType: 'geoserver'
      })
      dzialkiSource.on('imageloaderror', () => handleWmsLoadError('wms-dzialki', dzialkiUrl))
      dzialkiSource.on('imageloadend', () => handleWmsLoadSuccess('wms-dzialki'))

      const dzialkiLayer = new ImageLayer({
        source: dzialkiSource,
        opacity: 0.9 // Wysoka widoczność dla działek
      })
      ;(dzialkiLayer as any).set('id', 'wms-dzialki')
      map.addLayer(dzialkiLayer)
    }

    if (showBudynki) {
      const budynkiUrl = 'https://wms.epodgik.pl/cgi-bin/piaseczno-mz'
      const budynkiSource = new ImageWMS({
        url: budynkiUrl,
        params: {
          'LAYERS': 'budynki-etykiety,budynki-53__,budowle-teksty,budowle-etykiety,budowle-punkty,budowle-symbolep,budowle-symbole,budowle-obrysy',
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'VERSION': '1.1.1'
        },
        serverType: 'geoserver'
      })
      budynkiSource.on('imageloaderror', () => handleWmsLoadError('wms-budynki', budynkiUrl))
      budynkiSource.on('imageloadend', () => handleWmsLoadSuccess('wms-budynki'))

      const budynkiLayer = new ImageLayer({
        source: budynkiSource,
        opacity: 0.8
      })
      ;(budynkiLayer as any).set('id', 'wms-budynki')
      map.addLayer(budynkiLayer)
    }

    if (showWodociagi) {
      const scaleDenom = mapZoom >= 15 ? 100 : 1000
      // Dla warstwy tekstowej używamy znacznie mniejszej wartości SCALE_DENOMINATOR,
      // żeby tekst był większy i bardziej czytelny
      const textScaleDenom = mapZoom >= 15 ? 10 : 50

      const wodociagiLayers = [
        { id: 'wms-wodociagi-teksty', layers: 'wodociagi-teksty', scaleDenom: textScaleDenom },
        { id: 'wms-wodociagi-odnosniki', layers: 'wodociagi-odnosniki', scaleDenom: scaleDenom },
        { id: 'wms-wodociagi-etykiety', layers: 'wodociagi-etykiety', scaleDenom: textScaleDenom },
        { id: 'wms-wodociagi-powierzchniowe', layers: 'wodociagi-powierzchniowe', scaleDenom: scaleDenom },
        { id: 'wms-wodociagi-punktowe', layers: 'wodociagi-punktowe', scaleDenom: scaleDenom },
        { id: 'wms-wodociagi-liniowe', layers: 'wodociagi-liniowe', scaleDenom: scaleDenom }
      ]

      wodociagiLayers.forEach(({ id, layers, scaleDenom: layerScaleDenom }) => {
        const params: any = {
          'LAYERS': layers,
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'VERSION': '1.1.1',
          'SCALE_DENOMINATOR': layerScaleDenom
        }

        // Smart image loading for label layers - load once at zoom 17.5, scale afterwards
        const isLabelLayer = layers === 'wodociagi-etykiety' || layers === 'kanalizacja-etykiety'
        if (isLabelLayer) {
          // Load and cache image at zoom 17.5
          const currentZoom = map.getView().getZoom() || 0
          if (currentZoom >= 17.5 && currentZoom < 18) {
            // Load and cache image at zoom 17.5
            // image.getImage().src = src
            // imageCache.current[layers] = image.getImage()
          }
        }

        const wodociagiUrl = 'https://wms.epodgik.pl/cgi-bin/piaseczno-mz'
        const wodociagiSource = new ImageWMS({
          url: wodociagiUrl,
          params: params,
          serverType: 'geoserver'
        })
        wodociagiSource.on('imageloaderror', () => handleWmsLoadError(id, wodociagiUrl))
        wodociagiSource.on('imageloadend', () => handleWmsLoadSuccess(id))

        const layer = new ImageLayer({
          source: wodociagiSource,
          opacity: 0.8  // Przezroczystość dla wszystkich warstw wodociągów
        })
        ;(layer as any).set('id', id)
        map.addLayer(layer)


      })
    }

    if (showKanalizacja) {
      const kanalizacjaUrl = 'https://wms.epodgik.pl/cgi-bin/piaseczno-mz'
      const kanalizacjaSource = new ImageWMS({
        url: kanalizacjaUrl,
        params: {
          'LAYERS': 'kanalizacja-teksty,kanalizacja-odnosniki,kanalizacja-etykiety,kanalizacja-powierzchniowe,kanalizacja-liniowe,kanalizacja-punktowe',
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'VERSION': '1.1.1'
        },
        serverType: 'geoserver'
      })
      kanalizacjaSource.on('imageloaderror', () => handleWmsLoadError('wms-kanalizacja', kanalizacjaUrl))
      kanalizacjaSource.on('imageloadend', () => handleWmsLoadSuccess('wms-kanalizacja'))

      const kanalizacjaLayer = new ImageLayer({
        source: kanalizacjaSource,
        opacity: 0.8
      })
      ;(kanalizacjaLayer as any).set('id', 'wms-kanalizacja')
      map.addLayer(kanalizacjaLayer)
    }

    // Pozostałe sieci uzbrojenia terenu (mapa zasadnicza). Nazwy warstw potwierdzone
    // w GetCapabilities usługi piaseczno-mz (gaz-*, telekomunikacja-*, energia-*).
    const NETWORK_LAYERS: Array<{ id: string; layers: string; enabled: boolean }> = [
      { id: 'wms-gaz', enabled: showGaz,
        layers: 'gaz-teksty,gaz-odnosniki,gaz-etykiety,gaz-powierzchniowe,gaz-liniowe,gaz-punktowe' },
      { id: 'wms-teletechnika', enabled: showTeletechnika,
        layers: 'telekomunikacja-teksty,telekomunikacja-odnosniki,telekomunikacja-etykiety,telekomunikacja-powierzchniowe,telekomunikacja-liniowe,telekomunikacja-punktowe,telekomunikacja-napowietrzne,telekomunikacja-masztyp' },
      { id: 'wms-elektryka', enabled: showElektryka,
        layers: 'energia-teksty,energia-odnosniki,energia-etykiety,energia-powierzchniowe,energia-liniowe,energia-punktowe,energia-napowietrzne,energia-masztyl,energia-masztyp' },
    ]
    NETWORK_LAYERS.forEach(({ id, layers, enabled }) => {
      if (!enabled) return
      const netUrl = 'https://wms.epodgik.pl/cgi-bin/piaseczno-mz'
      const netSource = new ImageWMS({
        url: netUrl,
        params: { 'LAYERS': layers, 'FORMAT': 'image/png', 'TRANSPARENT': true, 'VERSION': '1.1.1' },
        serverType: 'geoserver'
      })
      netSource.on('imageloaderror', () => handleWmsLoadError(id, netUrl))
      netSource.on('imageloadend', () => handleWmsLoadSuccess(id))
      const netLayer = new ImageLayer({ source: netSource, opacity: 0.8 })
      ;(netLayer as any).set('id', id)
      map.addLayer(netLayer)
    })

    if (showGraniceMiejscowosci) {
      const graniceUrl = 'https://wms.epodgik.pl/cgi-bin/piaseczno'
      const graniceSource = new ImageWMS({
        url: graniceUrl,
        params: {
          'LAYERS': 'obreby',
          'FORMAT': 'image/png',
          'TRANSPARENT': true,
          'VERSION': '1.1.1'
        },
        serverType: 'geoserver'
      })
      graniceSource.on('imageloaderror', () => handleWmsLoadError('wms-granice-miejscowosci', graniceUrl))
      graniceSource.on('imageloadend', () => handleWmsLoadSuccess('wms-granice-miejscowosci'))

      const graniceMiejscowosciLayer = new ImageLayer({
        source: graniceSource,
        opacity: 0.7
      })
      ;(graniceMiejscowosciLayer as any).set('id', 'wms-granice-miejscowosci')
      map.addLayer(graniceMiejscowosciLayer)
    }
  }, [showDzialki, showBudynki, showRejestrWlasnosci, showWodociagi, showKanalizacja, showGaz, showTeletechnika, showElektryka, showGraniceMiejscowosci, mapZoom])

  // Update infrastructure markers
  useEffect(() => {
    if (!mapInstance.current) return

    const map = mapInstance.current
    const layers = map.getLayers().getArray()

    // Remove existing infrastructure layer
    const infraLayer = layers.find(layer => (layer as any).get('id') === 'infrastructure')
    if (infraLayer) {
      map.removeLayer(infraLayer)
    }

    if (showInfrastructure && infrastructureData.length > 0) {
      const vectorSource = new VectorSource()

      // Create tooltip overlay for infrastructure
      const tooltipElement = document.createElement('div')
      tooltipElement.className = 'bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-2 text-sm max-w-xs z-[2000]'
      tooltipElement.style.display = 'none'

      const tooltipOverlay = new Overlay({
        element: tooltipElement,
        positioning: 'bottom-center',
        stopEvent: false,
        offset: [0, -10]
      })
      map.addOverlay(tooltipOverlay)

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: (feature) => {
          const item = feature.get('infrastructureData') as InfrastructureObject
          const iconSvg = getInfrastructureIcon(item.infrastructure_type)

          return new Style({
            image: new Icon({
              src: 'data:image/svg+xml;base64,' + btoa(iconSvg),
              scale: 1,
              anchor: [0.5, 1] // Center bottom anchor for better positioning
            })
          })
        }
      })
      ;(vectorLayer as any).set('id', 'infrastructure')

      infrastructureData.forEach((item) => {
        const lat = parseFloat(item.latitude)
        const lng = parseFloat(item.longitude)

        if (!isNaN(lat) && !isNaN(lng)) {
          const feature = new Feature({
            geometry: new Point(fromLonLat([lng, lat])),
            infrastructureData: item
          })
          vectorSource.addFeature(feature)
        }
      })

      // Add hover interactions for tooltips
      let currentFeature: Feature | null = null

      map.on('pointermove', (event) => {
        const pixel = map.getEventPixel(event.originalEvent)
        const hit = map.hasFeatureAtPixel(pixel, {
          layerFilter: (layer) => (layer as any).get('id') === 'infrastructure'
        })

        const target = map.getTarget()
        if (target && typeof target === 'object' && 'style' in target) {
          (target as HTMLElement).style.cursor = hit ? 'pointer' : ''
        }

        if (hit) {
          const features = map.getFeaturesAtPixel(pixel, {
            layerFilter: (layer) => (layer as any).get('id') === 'infrastructure'
          })

          if (features.length > 0) {
            const feature = features[0] as Feature
            const item = feature.get('infrastructureData') as InfrastructureObject

            if (currentFeature !== feature) {
              currentFeature = feature

              // Generate tooltip content based on infrastructure type
              let tooltipContent = `
                <div class="font-semibold text-gray-900 mb-1">${item.name}</div>
                <div class="text-gray-600 mb-2">${item.infrastructure_type}</div>
                <div class="text-gray-500">Status: [placeholder]</div>
              `

              const normalizedType = item.infrastructure_type.toLowerCase().trim()

              if (normalizedType.includes('stacja') && normalizedType.includes('uzdatniania') && normalizedType.includes('wody')) {
                tooltipContent += `
                  <div class="text-gray-500 mt-1">Przepływ do sieci: [placeholder]</div>
                  <div class="text-gray-500">Ciśnienie w sieci: [placeholder]</div>
                `
              } else if (normalizedType.includes('oczyszczalnia') && normalizedType.includes('ścieków')) {
                tooltipContent += `
                  <div class="text-gray-500 mt-1">Parametr 1: [placeholder]</div>
                  <div class="text-gray-500">Parametr 2: [placeholder]</div>
                  <div class="text-gray-500">Parametr 3: [placeholder]</div>
                `
              }

              tooltipElement.innerHTML = tooltipContent
              tooltipElement.style.display = 'block'
              tooltipOverlay.setPosition(event.coordinate)
            }
          }
        } else {
          if (currentFeature) {
            currentFeature = null
            tooltipElement.style.display = 'none'
          }
        }
      })

      map.addLayer(vectorLayer)
    }
  }, [showInfrastructure, infrastructureData])

  // Update water segments layer
  useEffect(() => {
    if (!mapInstance.current) return

    const map = mapInstance.current
    const layers = map.getLayers().getArray()

    // Remove existing water segments layer
    const waterSegmentsLayer = layers.find(layer => (layer as any).get('id') === 'water-segments')
    if (waterSegmentsLayer) {
      map.removeLayer(waterSegmentsLayer)
    }

    if (showWaterSegments && waterSegmentsData.length > 0) {
      const vectorSource = new VectorSource()

      // Create tooltip overlay for water segments
      const tooltipElement = document.createElement('div')
      tooltipElement.className = 'bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-2 text-sm max-w-xs z-[2000]'
      tooltipElement.style.display = 'none'

      const tooltipOverlay = new Overlay({
        element: tooltipElement,
        positioning: 'bottom-center',
        stopEvent: false,
        offset: [0, -10]
      })
      map.addOverlay(tooltipOverlay)

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: (feature) => {
          const segment = feature.get('waterSegmentData')
          const isLine = feature.get('isLine')

          if (isLine) {
            // Different colors for different segment types
            let color = '#3B82F6' // Default blue
            let width = 3

            if (selectedSegment && segment.id === selectedSegment.id) {
              color = '#FF0000' // Bright red for selected segment
              width = 5 // Thicker line for selected segment
            } else if (segment.segment_type === 'MAGISTRALA') {
              color = '#DC2626' // Red for main lines
            } else if (segment.segment_type === 'ODNOGA') {
              color = '#EA580C' // Orange for branches
            } else if (segment.segment_type === 'PRZYŁĄCZE') {
              color = '#16A34A' // Green for connections
            }

            return new Style({
              stroke: new Stroke({
                color: color,
                width: width
              })
            })
          }
        }
      })
      ;(vectorLayer as any).set('id', 'water-segments')

      waterSegmentsData.forEach((segment) => {
        if (segment.geometry) {
          try {
            const geojson = JSON.parse(segment.geometry)
            const coordinates = geojson.coordinates

            if (coordinates && coordinates.length >= 2) {
              // Create line feature
              const lineCoords = coordinates.map((coord: [number, number]) => fromLonLat(coord))
              const lineFeature = new Feature({
                geometry: new LineString(lineCoords),
                waterSegmentData: segment,
                isLine: true
              })
              vectorSource.addFeature(lineFeature)

              // Create endpoint markers
              const startPoint = new Feature({
                geometry: new Point(fromLonLat(coordinates[0])),
                waterSegmentData: segment,
                isStartPoint: true
              })
              vectorSource.addFeature(startPoint)

              const endPoint = new Feature({
                geometry: new Point(fromLonLat(coordinates[coordinates.length - 1])),
                waterSegmentData: segment,
                isEndPoint: true
              })
              vectorSource.addFeature(endPoint)
            }
          } catch (error) {
            console.error('Error parsing segment geometry:', error)
          }
        }
      })

      // Add hover interactions for tooltips
      let currentFeature: Feature | null = null

      map.on('pointermove', (event) => {
        const pixel = map.getEventPixel(event.originalEvent)
        const hit = map.hasFeatureAtPixel(pixel, {
          layerFilter: (layer) => (layer as any).get('id') === 'water-segments'
        })

        const target = map.getTarget()
        if (target && typeof target === 'object' && 'style' in target) {
          (target as HTMLElement).style.cursor = hit ? 'pointer' : ''
        }

        if (hit) {
          const features = map.getFeaturesAtPixel(pixel, {
            layerFilter: (layer) => (layer as any).get('id') === 'water-segments'
          })

          if (features.length > 0) {
            const feature = features[0] as Feature
            const segment = feature.get('waterSegmentData')

            if (currentFeature !== feature && segment) {
              currentFeature = feature

              const tooltipContent = `
                <div class="font-semibold text-gray-900 mb-1">${segment.name}</div>
                <div class="text-gray-600 mb-1">${segment.segment_type}</div>
                <div class="text-gray-500 mb-1">Długość: ${segment.length_meters || 'N/A'} m</div>
                <div class="text-gray-500 mb-1">Średnica: ${segment.diameter_mm || 'N/A'} mm</div>
                <div class="text-gray-500">Materiał: ${segment.material || 'N/A'}</div>
              `

              tooltipElement.innerHTML = tooltipContent
              tooltipElement.style.display = 'block'
              tooltipOverlay.setPosition(event.coordinate)
            }
          }
        } else {
          if (currentFeature) {
            currentFeature = null
            tooltipElement.style.display = 'none'
          }
        }
      })

      map.addLayer(vectorLayer)
    }
  }, [showWaterSegments, waterSegmentsData, selectedSegment])

  // Update sewer segments layer
  useEffect(() => {
    if (!mapInstance.current) return

    const map = mapInstance.current
    const layers = map.getLayers().getArray()

    // Remove existing sewer segments layer
    const sewerSegmentsLayer = layers.find(layer => (layer as any).get('id') === 'sewer-segments')
    if (sewerSegmentsLayer) {
      map.removeLayer(sewerSegmentsLayer)
    }

    if (showSewerSegments && sewerSegmentsData.length > 0) {
      const vectorSource = new VectorSource()

      // Create tooltip overlay for sewer segments
      const tooltipElement = document.createElement('div')
      tooltipElement.className = 'bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-2 text-sm max-w-xs z-[2000]'
      tooltipElement.style.display = 'none'

      const tooltipOverlay = new Overlay({
        element: tooltipElement,
        positioning: 'bottom-center',
        stopEvent: false,
        offset: [0, -10]
      })
      map.addOverlay(tooltipOverlay)

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: (feature) => {
          const segment = feature.get('sewerSegmentData')
          const isLine = feature.get('isLine')

          if (isLine) {
            // Different colors for different segment types
            let color = '#8B4513' // Default brown
            let width = 3

            if (selectedSegment && segment.id === selectedSegment.id) {
              color = '#FF0000' // Bright red for selected segment
              width = 5 // Thicker line for selected segment
            } else if (segment.segment_type === 'MAGISTRALA') {
              color = '#A0522D' // Brown for main lines
            } else if (segment.segment_type === 'ODNOGA') {
              color = '#CD853F' // Orange-brown for branches
            } else if (segment.segment_type === 'PRZYŁĄCZE') {
              color = '#D2691E' // Chocolate for connections
            }

            return new Style({
              stroke: new Stroke({
                color: color,
                width: width
              })
            })
          }
        }
      })
      ;(vectorLayer as any).set('id', 'sewer-segments')

      sewerSegmentsData.forEach((segment) => {
        if (segment.geometry) {
          try {
            const geojson = JSON.parse(segment.geometry)
            const coordinates = geojson.coordinates

            if (coordinates && coordinates.length >= 2) {
              // Create line feature
              const lineCoords = coordinates.map((coord: [number, number]) => fromLonLat(coord))
              const lineFeature = new Feature({
                geometry: new LineString(lineCoords),
                sewerSegmentData: segment,
                isLine: true
              })
              vectorSource.addFeature(lineFeature)

              // Create endpoint markers
              const startPoint = new Feature({
                geometry: new Point(fromLonLat(coordinates[0])),
                sewerSegmentData: segment,
                isStartPoint: true
              })
              vectorSource.addFeature(startPoint)

              const endPoint = new Feature({
                geometry: new Point(fromLonLat(coordinates[coordinates.length - 1])),
                sewerSegmentData: segment,
                isEndPoint: true
              })
              vectorSource.addFeature(endPoint)
            }
          } catch (error) {
            console.error('Error parsing sewer segment geometry:', error)
          }
        }
      })

      // Add hover interactions for tooltips
      let currentFeature: Feature | null = null

      map.on('pointermove', (event) => {
        const pixel = map.getEventPixel(event.originalEvent)
        const hit = map.hasFeatureAtPixel(pixel, {
          layerFilter: (layer) => (layer as any).get('id') === 'sewer-segments'
        })

        const target = map.getTarget()
        if (target && typeof target === 'object' && 'style' in target) {
          (target as HTMLElement).style.cursor = hit ? 'pointer' : ''
        }

        if (hit) {
          const features = map.getFeaturesAtPixel(pixel, {
            layerFilter: (layer) => (layer as any).get('id') === 'sewer-segments'
          })

          if (features.length > 0) {
            const feature = features[0] as Feature
            const segment = feature.get('sewerSegmentData')

            if (currentFeature !== feature && segment) {
              currentFeature = feature

              const tooltipContent = `
                <div class="font-semibold text-gray-900 mb-1">${segment.name}</div>
                <div class="text-gray-600 mb-1">${segment.segment_type}</div>
                <div class="text-gray-500 mb-1">Długość: ${segment.length_meters || 'N/A'} m</div>
                <div class="text-gray-500 mb-1">Średnica: ${segment.diameter_mm || 'N/A'} mm</div>
                <div class="text-gray-500">Materiał: ${segment.material || 'N/A'}</div>
              `

              tooltipElement.innerHTML = tooltipContent
              tooltipElement.style.display = 'block'
              tooltipOverlay.setPosition(event.coordinate)
            }
          }
        } else {
          if (currentFeature) {
            currentFeature = null
            tooltipElement.style.display = 'none'
          }
        }
      })

      map.addLayer(vectorLayer)
    }
  }, [showSewerSegments, sewerSegmentsData, selectedSegment])

  // Update realizations layer
  useEffect(() => {
    if (!mapInstance.current) return

    const map = mapInstance.current
    const layers = map.getLayers().getArray()

    // Remove existing realizations layer
    const realizationsLayer = layers.find(layer => (layer as any).get('id') === 'realizations')
    if (realizationsLayer) {
      map.removeLayer(realizationsLayer)
    }

    if (showRealizations && realizationsData.length > 0) {
      const vectorSource = new VectorSource()

      // Create tooltip overlay for realizations
      const tooltipElement = document.createElement('div')
      tooltipElement.className = 'bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-2 text-sm max-w-xs z-[2000]'
      tooltipElement.style.display = 'none'

      const tooltipOverlay = new Overlay({
        element: tooltipElement,
        positioning: 'bottom-center',
        stopEvent: false,
        offset: [0, -10]
      })
      map.addOverlay(tooltipOverlay)

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: (feature) => {
          const realization = feature.get('realizationData')
          const isPoint = feature.get('isPoint')

          if (isPoint) {
            // Point markers with different colors for connection types
            const markerColor = realization.connection_type === 'water' ? '#0066CC' : '#8B4513'
            const connectionTypeText = realization.connection_type === 'water' ? 'Wodociąg' : 'Kanalizacja'

            return new Style({
              image: new Icon({
                src: 'data:image/svg+xml;base64,' + btoa(`
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" fill="${markerColor}"/>
                    <circle cx="12" cy="10" r="3" fill="white"/>
                  </svg>
                `),
                scale: 0.8
              })
            })
          } else {
            // Line features (legacy - for backward compatibility)
            let color = '#FF6B35' // Default orange for realizations
            let width = 4

            if (realization.connection_type === 'water') {
              color = '#3B82F6' // Blue for water connections
            } else if (realization.connection_type === 'sewage') {
              color = '#8B4513' // Brown for sewage connections
            }

            return new Style({
              stroke: new Stroke({
                color: color,
                width: width
              })
            })
          }
        }
      })
      ;(vectorLayer as any).set('id', 'realizations')

      realizationsData.forEach((realization) => {
        if (realization.geometry) {
          try {
            const geojson = JSON.parse(realization.geometry)

            if (geojson.type === 'Point') {
              // Handle Point geometry
              const coordinates = geojson.coordinates
              if (coordinates && coordinates.length === 2) {
                const [lng, lat] = coordinates
                const pointFeature = new Feature({
                  geometry: new Point(fromLonLat([lng, lat])),
                  realizationData: realization,
                  isPoint: true
                })
                vectorSource.addFeature(pointFeature)
              }
            } else if (geojson.type === 'LineString') {
              // Handle LineString geometry (legacy)
              const coordinates = geojson.coordinates
              if (coordinates && coordinates.length >= 2) {
                const lineCoords = coordinates.map((coord: [number, number]) => fromLonLat(coord))
                const lineFeature = new Feature({
                  geometry: new LineString(lineCoords),
                  realizationData: realization,
                  isPoint: false
                })
                vectorSource.addFeature(lineFeature)
              }
            }
          } catch (error) {
            console.error('Error parsing realization geometry:', error)
          }
        }
      })

      // Add hover interactions for tooltips
      let currentFeature: Feature | null = null

      map.on('pointermove', (event) => {
        const pixel = map.getEventPixel(event.originalEvent)
        const hit = map.hasFeatureAtPixel(pixel, {
          layerFilter: (layer) => (layer as any).get('id') === 'realizations'
        })

        const target = map.getTarget()
        if (target && typeof target === 'object' && 'style' in target) {
          (target as HTMLElement).style.cursor = hit ? 'pointer' : ''
        }

        if (hit) {
          const features = map.getFeaturesAtPixel(pixel, {
            layerFilter: (layer) => (layer as any).get('id') === 'realizations'
          })

          if (features.length > 0) {
            const feature = features[0] as Feature
            const realization = feature.get('realizationData')

            if (currentFeature !== feature && realization) {
              currentFeature = feature

              const tooltipContent = `
                <div class="font-semibold text-gray-900 mb-1">${realization.applicant_name}</div>
                <div class="text-gray-600 mb-1">${realization.location}, dz. ${realization.plot_number}</div>
                <div class="text-gray-500 mb-1">Typ: ${realization.connection_type === 'water' ? 'Wodociąg' : 'Kanalizacja'}</div>
                <div class="text-gray-500">Status: ${realization.status === 'active' ? 'Aktywna' : realization.status === 'completed' ? 'Zakończona' : 'Wersja robocza'}</div>
              `

              tooltipElement.innerHTML = tooltipContent
              tooltipElement.style.display = 'block'
              tooltipOverlay.setPosition(event.coordinate)
            }
          }
        } else {
          if (currentFeature) {
            currentFeature = null
            tooltipElement.style.display = 'none'
          }
        }
      })

      map.addLayer(vectorLayer)
    }
  }, [showRealizations, realizationsData])

  // Dynamic scaling for high zoom levels - prevent thin lines at zoom >= 18
  useEffect(() => {
    if (!mapInstance.current || !showWodociagi) return

    const map = mapInstance.current
    const layers = map.getLayers().getArray()

    // Find wodociagi layers and update their SCALE_DENOMINATOR for high zoom
    const wodociagiLayerIds = [
      'wms-wodociagi-teksty',
      'wms-wodociagi-odnosniki',
      'wms-wodociagi-etykiety',
      'wms-wodociagi-powierzchniowe',
      'wms-wodociagi-punktowe',
      'wms-wodociagi-liniowe'
    ]

    wodociagiLayerIds.forEach(layerId => {
      const layer = layers.find(l => (l as any).get('id') === layerId) as ImageLayer<ImageWMS>
      if (layer) {
        const source = layer.getSource()
        if (source) {
          const currentParams = source.getParams()

          // Dynamic scaling to keep lines thick at all zoom levels
          // Stop updating at zoom 20 to prevent reloading at zoom 21
          let newScaleDenom: number
          if (mapZoom >= 20) {
            newScaleDenom = 1  // Ultra high zoom - maximum thickness, no more updates beyond this
          } else if (mapZoom >= 18) {
            newScaleDenom = 5  // High zoom - very thick lines
          } else if (mapZoom >= 15) {
            newScaleDenom = 50  // Medium zoom - thick lines
          } else {
            newScaleDenom = 500  // Low zoom - normal lines
          }

          // Only update if zoom is <= 20 to prevent reloading at zoom 21
          if (mapZoom <= 20 && currentParams['SCALE_DENOMINATOR'] !== newScaleDenom) {
            source.updateParams({
              'SCALE_DENOMINATOR': newScaleDenom
            })
          }
        }


      }
    })
  }, [mapZoom, showWodociagi])

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  const resetView = () => {
    if (mapInstance.current) {
      const view = mapInstance.current.getView()
      view.setCenter(fromLonLat(DEFAULT_CENTER))
      view.setZoom(DEFAULT_ZOOM)
    }
    setMapCenter(DEFAULT_CENTER)
    setMapZoom(DEFAULT_ZOOM)
    localStorage.removeItem('mapCenter')
    localStorage.removeItem('mapZoom')
  }

  // Fetch infrastructure data
  const fetchInfrastructure = async () => {
    if (!token) return

    setLoadingInfrastructure(true)
    try {
      const response = await fetch('/api/infrastructure/', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        console.log('Infrastructure data received:', data)

        // Filter objects that have valid coordinates
        const validInfrastructure = data.filter((item: InfrastructureObject) => {
          const lat = item.latitude?.toString().replace(',', '.').trim()
          const lng = item.longitude?.toString().replace(',', '.').trim()

          const isValidLat = lat && lat !== '' && !isNaN(parseFloat(lat))
          const isValidLng = lng && lng !== '' && !isNaN(parseFloat(lng))

          if (!isValidLat || !isValidLng) {
            console.log('Filtered out infrastructure item:', item.name, 'lat:', lat, 'lng:', lng)
          }

          return isValidLat && isValidLng
        })

        console.log('Valid infrastructure items:', validInfrastructure.length, 'out of', data.length)
        setInfrastructureData(validInfrastructure)
      } else {
        console.error('Failed to fetch infrastructure data')
      }
    } catch (error) {
      console.error('Error fetching infrastructure:', error)
    } finally {
      setLoadingInfrastructure(false)
    }
  }

  // Fetch water segments data
  const fetchWaterSegments = async () => {
    if (!token) return

    setLoadingWaterSegments(true)
    try {
      const response = await fetch('/api/water-segments/', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        console.log('Water segments data received:', data)
        setWaterSegmentsData(data)
      } else {
        console.error('Failed to fetch water segments data')
      }
    } catch (error) {
      console.error('Error fetching water segments:', error)
    } finally {
      setLoadingWaterSegments(false)
    }
  }

  // Fetch sewer segments data
  const fetchSewerSegments = async () => {
    if (!token) return

    setLoadingSewerSegments(true)
    try {
      const response = await fetch('/api/sewer-segments/', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        console.log('Sewer segments data received:', data)
        setSewerSegmentsData(data)
      } else {
        console.error('Failed to fetch sewer segments data')
      }
    } catch (error) {
      console.error('Error fetching sewer segments:', error)
    } finally {
      setLoadingSewerSegments(false)
    }
  }

  // Fetch realizations data
  const fetchRealizations = async () => {
    if (!token) return

    setLoadingRealizations(true)
    try {
      const response = await fetch('/api/realizations/map-data', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        console.log('Realizations data received:', data)
        setRealizationsData(data)
      } else {
        console.error('Failed to fetch realizations data')
      }
    } catch (error) {
      console.error('Error fetching realizations:', error)
    } finally {
      setLoadingRealizations(false)
    }
  }

  // Handle infrastructure toggle
  const handleToggleInfrastructure = () => {
    const newState = !showInfrastructure
    setShowInfrastructure(newState)

    if (newState && infrastructureData.length === 0) {
      fetchInfrastructure()
    }
  }

  // Handle powiatowe data toggles
  const handleToggleDzialki = () => setShowDzialki(!showDzialki)
  const handleToggleBudynki = () => setShowBudynki(!showBudynki)
  const handleToggleRejestrWlasnosci = () => setShowRejestrWlasnosci(!showRejestrWlasnosci)
  const handleToggleGaz = () => setShowGaz(!showGaz)
  const handleToggleTeletechnika = () => setShowTeletechnika(!showTeletechnika)
  const handleToggleElektryka = () => setShowElektryka(!showElektryka)
  const handleToggleFeatureInfo = () => {
    const next = !featureInfoMode
    setFeatureInfoMode(next)
    if (!next) setFeatureInfoData(null)
    if (next) showInfo('Informacje o obiekcie', 'Kliknij obiekt sieci na mapie, aby zobaczyć jego dane (średnica, opis).')
  }

  // === Warstwa "Przepływy" (telemetria) ===
  const fetchFlows = async () => {
    if (!token) return
    try {
      const response = await fetch('/api/measurement-points/live', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) setFlowsData(await response.json())
    } catch (error) {
      console.error('Error fetching flows:', error)
    }
  }

  const handleToggleFlows = () => {
    const next = !showFlows
    setShowFlows(next)
    if (next) fetchFlows()
  }

  // Warstwy obiektowe do zapytania GetFeatureInfo. Zapytanie zawsze obejmuje
  // WSZYSTKIE sieci uzbrojenia (jak na e-mapa.net) - niezależnie od tego, które
  // warstwy są aktualnie włączone, żeby klik w dowolny obiekt coś zwracał.
  const FEATURE_INFO_LAYERS = [
    'wodociagi-liniowe', 'wodociagi-punktowe', 'wodociagi-powierzchniowe',
    'kanalizacja-liniowe', 'kanalizacja-punktowe', 'kanalizacja-powierzchniowe',
    'gaz-liniowe', 'gaz-punktowe', 'gaz-powierzchniowe',
    'telekomunikacja-liniowe', 'telekomunikacja-punktowe', 'telekomunikacja-powierzchniowe', 'telekomunikacja-napowietrzne',
    'energia-liniowe', 'energia-punktowe', 'energia-powierzchniowe', 'energia-napowietrzne',
  ].join(',')

  // GetFeatureInfo (text/html) zwraca opakowanie z ramkami <iframe src="...gfi.php?...">.
  // Wyciągamy z nich adresy tabel atrybutów.
  const extractGfiSrcs = (html: string): string[] => {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html')
      return Array.from(doc.querySelectorAll('iframe'))
        .map(f => f.getAttribute('src') || '')
        .filter(src => src.includes('gfi.php'))
    } catch {
      return []
    }
  }

  // Sanityzuje HTML tabeli atrybutów: usuwa skrypty/style/ramki i atrybuty
  // wykonywalne, zostawia strukturę tabeli. Zwraca bezpieczny HTML + czysty tekst.
  const sanitizeFeatureInfoHtml = (raw: string): { html: string; text: string } => {
    try {
      const doc = new DOMParser().parseFromString(raw, 'text/html')
      doc.querySelectorAll('script,style,link,meta,base,form,input,iframe,object,embed').forEach(e => e.remove())
      doc.querySelectorAll('*').forEach(el => {
        Array.from(el.attributes).forEach(attr => {
          const n = attr.name.toLowerCase()
          if (n.startsWith('on') || n === 'href' || n === 'src' || n === 'style' || n === 'class' || n === 'id' || n === 'target') {
            el.removeAttribute(attr.name)
          }
        })
      })
      const body = doc.body
      return { html: body ? body.innerHTML.trim() : '', text: (body?.textContent || '').replace(/\s+/g, ' ').trim() }
    } catch {
      return { html: '', text: '' }
    }
  }

  const handleFeatureInfoClick = async (event: any) => {
    const map = mapInstance.current
    const coord = toLonLat(event.coordinate) as [number, number]
    // Panel pokazujemy OD RAZU (stan ładowania) - żeby zawsze była reakcja na klik.
    setFeatureInfoData({ coord, loading: true })
    if (!map) return
    const view = map.getView()
    const size = map.getSize()
    if (!size) {
      setFeatureInfoData({ coord, error: 'Brak rozmiaru mapy.' })
      return
    }

    try {
      const extent = view.calculateExtent(size)
      const px = event.pixel || map.getPixelFromCoordinate(event.coordinate)
      // Ręczne złożenie zapytania WMS 1.1.1 GetFeatureInfo (pewniejsze niż getFeatureInfoUrl)
      const params = new URLSearchParams({
        SERVICE: 'WMS',
        VERSION: '1.1.1',
        REQUEST: 'GetFeatureInfo',
        LAYERS: FEATURE_INFO_LAYERS,
        QUERY_LAYERS: FEATURE_INFO_LAYERS,
        SRS: 'EPSG:3857',
        BBOX: extent.join(','),
        WIDTH: String(Math.round(size[0])),
        HEIGHT: String(Math.round(size[1])),
        X: String(Math.round(px[0])),
        Y: String(Math.round(px[1])),
        INFO_FORMAT: 'text/html',
        FEATURE_COUNT: '25',
      })
      const infoUrl = `/api/wms-feature-info?${params.toString()}`
      const resp = await fetch(infoUrl)
      if (!resp.ok) {
        setFeatureInfoData({ coord, error: `Usługa niedostępna (HTTP ${resp.status}). Jeśli to 404 - zrestartuj backend (doszedł nowy endpoint).` })
        return
      }
      const wrapper = await resp.text()
      // Krok 2: z ramek wyciągamy adresy tabel atrybutów i dociągamy każdą przez proxy
      const srcs = extractGfiSrcs(wrapper)
      if (srcs.length === 0) {
        // Brak ramek - albo pusto, albo serwis zwrócił treść inline
        const inline = sanitizeFeatureInfoHtml(wrapper)
        setFeatureInfoData({ html: inline.text ? inline.html : '', coord })
        return
      }

      const parts = await Promise.all(srcs.map(async (src) => {
        try {
          const u = new URL(src, 'https://wms02.epodgik.pl')
          const teryt = u.searchParams.get('teryt') || ''
          const kod = u.searchParams.get('kod') || ''
          const obiekt = u.searchParams.get('obiekt') || ''
          if (!/^\d+$/.test(teryt) || !/^\d+$/.test(kod) || !/^\d+$/.test(obiekt)) return ''
          const r = await fetch(`/api/wms-gfi?teryt=${teryt}&kod=${kod}&obiekt=${obiekt}`)
          if (!r.ok) return ''
          return sanitizeFeatureInfoHtml(await r.text()).html
        } catch {
          return ''
        }
      }))

      const html = parts.filter(Boolean).join('<hr/>')
      setFeatureInfoData({ html, coord })
    } catch (e) {
      console.error('[GetFeatureInfo] błąd:', e)
      setFeatureInfoData({ coord, error: 'Nie udało się pobrać informacji o obiekcie.' })
    }
  }
  const handleToggleWodociagi = () => setShowWodociagi(!showWodociagi)
  const handleToggleKanalizacja = () => setShowKanalizacja(!showKanalizacja)
  const handleToggleGraniceMiejscowosci = () => setShowGraniceMiejscowosci(!showGraniceMiejscowosci)

  // Handle edit mode toggles
  const handleToggleEditMode = () => setEditMode(!editMode)
  const handleToggleWaterSegments = () => {
    const newState = !showWaterSegments
    setShowWaterSegments(newState)

    if (newState && waterSegmentsData.length === 0) {
      fetchWaterSegments()
    }
  }

  const handleToggleSewerSegments = () => {
    const newState = !showSewerSegments
    setShowSewerSegments(newState)

    if (newState && sewerSegmentsData.length === 0) {
      fetchSewerSegments()
    }
  }

  // Handle drawing mode
  const handleStartDrawingSegment = () => {
    if (!editMode) return

    setDrawingMode(true)
    setDrawingPoints([])
    setShowDrawingControls(false)

    // Change cursor to crosshair
    if (mapInstance.current) {
      const target = mapInstance.current.getTarget()
      if (target && typeof target === 'object' && 'style' in target) {
        (target as HTMLElement).style.cursor = 'crosshair'
      }
    }
  }

  // Handle editing mode
  const handleStartEditingSegment = () => {
    if (!editMode) return

    setEditingMode(true)
    setEditingSegment(null)

    // Change cursor to pointer for selecting segments
    if (mapInstance.current) {
      const target = mapInstance.current.getTarget()
      if (target && typeof target === 'object' && 'style' in target) {
        (target as HTMLElement).style.cursor = 'pointer'
      }
    }

    alert('Kliknij na odcinek wodociągu, który chcesz edytować')
  }

  // Handle creating measurement point
  const handleStartCreatingPoint = () => {
    // Narzędzie jest widoczne tylko w trybie edycji - nie blokujemy dodatkowo
    // na editMode (unikamy desynchronizacji zakładki Edycja z flagą editMode).
    setCreatingPointMode(true)

    if (mapInstance.current) {
      const target = mapInstance.current.getTarget()
      if (target && typeof target === 'object' && 'style' in target) {
        (target as HTMLElement).style.cursor = 'crosshair'
      }
    }

    showInfo('Dodawanie punktu pomiarowego', 'Kliknij na mapie w miejscu, gdzie chcesz umieścić punkt.')
  }

  // === Network Valves ===
  const fetchNetworkValves = async () => {
    if (!token) return
    setLoadingNetworkValves(true)
    try {
      const response = await fetch('/api/network-valves/', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setNetworkValvesData(data)
      }
    } catch (error) {
      console.error('Error fetching network valves:', error)
    } finally {
      setLoadingNetworkValves(false)
    }
  }

  const handleToggleNetworkValves = () => {
    const newState = !showNetworkValves
    setShowNetworkValves(newState)
    if (newState && networkValvesData.length === 0) {
      fetchNetworkValves()
    }
  }

  // Keep ref always up to date - opens modal instead of showConfirm
  handleToggleValveStatusRef.current = (valve: any) => {
    setValveForStatusChange(valve)
  }

  // Handle valve status change confirmed from modal (with reason)
  const handleValveStatusConfirm = async (reason: string) => {
    if (!valveForStatusChange) return

    const valve = valveForStatusChange
    const newStatus = valve.status === 'open' ? 'closed' : 'open'
    const statusPl = newStatus === 'open' ? 'OTWARTA' : 'ZAMKNIĘTA'

    try {
      const response = await fetch(`/api/network-valves/${valve.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus, reason })
      })
      if (response.ok) {
        showSuccess('Stan zasuwy zmieniony', `Zasuwa "${valve.name}" → ${statusPl}`)
        fetchNetworkValves()
      } else {
        const err = await response.json()
        showError('Błąd', err.detail || 'Nie udało się zmienić stanu zasuwy')
      }
    } catch (error) {
      console.error('Error toggling valve status:', error)
      showError('Błąd', 'Wystąpił problem z połączeniem')
    } finally {
      setValveForStatusChange(null)
    }
  }


  // Handle map click for valve placement - opens modal
  const handleMapClickForValvePlacement = (event: any) => {
    if (!placingValveMode || !mapInstance.current) return

    const map = mapInstance.current
    const coordinates = toLonLat(event.coordinate)

    // Reset mode and cursor
    setPlacingValveMode(false)
    const target = map.getTarget()
    if (target && typeof target === 'object' && 'style' in target) {
      (target as HTMLElement).style.cursor = ''
    }

    // Store coordinates and open modal
    setNewValveCoordinates([coordinates[0], coordinates[1]])
    setShowValveModal(true)
  }

  // Save valve from modal
  const handleSaveValve = async (data: { name: string; description: string; status: string }) => {
    if (!newValveCoordinates) return

    try {
      const response = await fetch('/api/network-valves/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          client_id: 1,
          name: data.name,
          description: data.description || null,
          latitude: newValveCoordinates[1].toFixed(6),
          longitude: newValveCoordinates[0].toFixed(6),
          status: data.status
        })
      })

      if (response.ok) {
        setShowValveModal(false)
        setNewValveCoordinates(null)
        if (!showNetworkValves) setShowNetworkValves(true)
        fetchNetworkValves()
        const statusPl = data.status === 'open' ? 'otwarta' : 'zamknięta'
        showSuccess('Zasuwa utworzona', `"${data.name}" (stan: ${statusPl})`)
      } else {
        const err = await response.json()
        showError('Błąd', err.detail || 'Nie udało się utworzyć zasuwy')
      }
    } catch (error) {
      console.error('Error creating valve:', error)
      showError('Błąd', 'Wystąpił problem z połączeniem')
    }
  }

  const handleStartPlacingValve = () => {
    if (!editMode) return
    setPlacingValveMode(true)
    if (mapInstance.current) {
      const target = mapInstance.current.getTarget()
      if (target && typeof target === 'object' && 'style' in target) {
        (target as HTMLElement).style.cursor = 'crosshair'
      }
    }
    showInfo('Tryb dodawania zasuwy', 'Kliknij na mapie, aby umieścić zasuwę sieciową')
  }

  // === Network Hydrants (hydranty) ===
  // Pobierz aktywny referat użytkownika (do przypisywania nowych obiektów)
  useEffect(() => {
    if (!token) return
    fetch('/api/users/me/context', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (data && data.client_id != null) setMapClientId(data.client_id) })
      .catch(() => {})
  }, [token])

  const fetchNetworkHydrants = async () => {
    if (!token) return
    try {
      const response = await fetch('/api/network-hydrants/', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setNetworkHydrantsData(data)
      }
    } catch (error) {
      console.error('Error fetching network hydrants:', error)
    }
  }

  const handleToggleNetworkHydrants = () => {
    const newState = !showNetworkHydrants
    setShowNetworkHydrants(newState)
    if (newState && networkHydrantsData.length === 0) {
      fetchNetworkHydrants()
    }
  }

  // Klik w istniejący hydrant -> modal edycji
  handleHydrantClickRef.current = (hydrant: any) => {
    setHydrantForEdit(hydrant)
  }

  const handleStartPlacingHydrant = () => {
    if (!editMode) return
    setPlacingHydrantMode(true)
    if (mapInstance.current) {
      const target = mapInstance.current.getTarget()
      if (target && typeof target === 'object' && 'style' in target) {
        (target as HTMLElement).style.cursor = 'crosshair'
      }
    }
    showInfo('Tryb dodawania hydrantu', 'Kliknij na mapie, aby umieścić hydrant')
  }

  const handleMapClickForHydrantPlacement = (event: any) => {
    if (!placingHydrantMode || !mapInstance.current) return
    const map = mapInstance.current
    const coordinates = toLonLat(event.coordinate)

    setPlacingHydrantMode(false)
    const target = map.getTarget()
    if (target && typeof target === 'object' && 'style' in target) {
      (target as HTMLElement).style.cursor = ''
    }

    setNewHydrantCoordinates([coordinates[0], coordinates[1]])
    setShowHydrantModal(true)
  }

  const handleSaveHydrant = async (data: { name: string; address: string; is_flushing_point: boolean; notes: string; inspection_status: string }) => {
    if (!newHydrantCoordinates) return
    try {
      const response = await fetch('/api/network-hydrants/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          client_id: mapClientId ?? undefined,
          name: data.name || null,
          address: data.address || null,
          latitude: newHydrantCoordinates[1].toFixed(6),
          longitude: newHydrantCoordinates[0].toFixed(6),
          is_flushing_point: data.is_flushing_point,
          notes: data.notes || null,
          inspection_status: data.inspection_status || null,
        })
      })
      if (response.ok) {
        const created = await response.json()
        setShowHydrantModal(false)
        setNewHydrantCoordinates(null)
        if (!showNetworkHydrants) setShowNetworkHydrants(true)
        fetchNetworkHydrants()
        showSuccess('Hydrant utworzony', `"${created.name || data.name}"` + (data.is_flushing_point ? ' (punkt płukania)' : ''))
      } else {
        const err = await response.json()
        showError('Błąd', err.detail || 'Nie udało się utworzyć hydrantu')
      }
    } catch (error) {
      console.error('Error creating hydrant:', error)
      showError('Błąd', 'Wystąpił problem z połączeniem')
    }
  }

  const handleUpdateHydrant = async (id: number, data: { name: string; address: string; is_flushing_point: boolean; notes: string; inspection_status: string }) => {
    try {
      const response = await fetch(`/api/network-hydrants/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: data.name,
          address: data.address || null,
          is_flushing_point: data.is_flushing_point,
          notes: data.notes || null,
          inspection_status: data.inspection_status || null,
        })
      })
      if (response.ok) {
        setHydrantForEdit(null)
        fetchNetworkHydrants()
        showSuccess('Hydrant zaktualizowany', `"${data.name}"`)
      } else {
        const err = await response.json()
        showError('Błąd', err.detail || 'Nie udało się zaktualizować hydrantu')
      }
    } catch (error) {
      console.error('Error updating hydrant:', error)
      showError('Błąd', 'Wystąpił problem z połączeniem')
    }
  }

  const handleDeleteHydrant = async (id: number) => {
    try {
      const response = await fetch(`/api/network-hydrants/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        setHydrantForEdit(null)
        fetchNetworkHydrants()
        showSuccess('Hydrant usunięty', '')
      } else {
        const err = await response.json()
        showError('Błąd', err.detail || 'Nie udało się usunąć hydrantu')
      }
    } catch (error) {
      console.error('Error deleting hydrant:', error)
      showError('Błąd', 'Wystąpił problem z połączeniem')
    }
  }

  // === Kontrolki mapy (mobile-friendly) ===
  const zoomBy = (delta: number) => {
    const view = mapInstance.current?.getView()
    if (!view) return
    const currentZoom = view.getZoom()
    if (currentZoom !== undefined) {
      view.animate({ zoom: currentZoom + delta, duration: 200 })
    }
  }

  const centerOnUserLocation = () => {
    // Klik = gest użytkownika: na iOS przy okazji prosimy o zgodę na kompas
    if (deviceLocation.needsOrientationPermission) {
      deviceLocation.requestOrientationPermission?.()
    }
    if (!showUserLocation) setShowUserLocation(true)
    const loc = deviceLocation.location
    const view = mapInstance.current?.getView()
    if (loc && view) {
      // Jeśli już wyśrodkowani i śledzimy - drugi klik wyłącza śledzenie
      if (followMode) {
        setFollowMode(false)
        return
      }
      view.animate({
        center: fromLonLat([loc.longitude, loc.latitude]),
        zoom: Math.max(view.getZoom() || 0, 17),
        duration: 500
      })
      setFollowMode(true)
    } else {
      deviceLocation.startTracking()
      setFollowMode(true)
      showInfo('Lokalizacja', 'Pobieranie pozycji GPS… mapa wyśrodkuje się automatycznie.')
    }
  }

  // Tryb "śledź mnie": auto-wyśrodkowanie mapy przy każdej aktualizacji pozycji
  useEffect(() => {
    if (!followMode) return
    const loc = deviceLocation.location
    const view = mapInstance.current?.getView()
    if (loc && view) {
      view.setCenter(fromLonLat([loc.longitude, loc.latitude]))
    }
  }, [followMode, deviceLocation.location])

  // Ręczne przesunięcie mapy wyłącza śledzenie (żeby mapa nie "uciekała" spod palca)
  useEffect(() => {
    if (!followMode) return
    const map = mapInstance.current
    if (!map) return
    const onDragStart = () => setFollowMode(false)
    map.on('pointerdrag', onDragStart)
    return () => { map.un('pointerdrag', onDragStart) }
  }, [followMode])

  // Anulowanie trybu dodawania punktu (zasuwa/hydrant) z banera na mapie
  const cancelPlacementMode = () => {
    setPlacingValveMode(false)
    setPlacingHydrantMode(false)
    if (mapInstance.current) {
      const target = mapInstance.current.getTarget()
      if (target && typeof target === 'object' && 'style' in target) {
        (target as HTMLElement).style.cursor = ''
      }
    }
  }

  // Handle realizations toggle
  const handleToggleRealizations = () => {
    const newState = !showRealizations
    setShowRealizations(newState)

    if (newState && realizationsData.length === 0) {
      fetchRealizations()
    }
  }

  // Handle map clicks for point creation - place point on segment
  const handleMapClickForPointCreation = (event: any) => {
    if (!mapInstance.current) return

    const map = mapInstance.current
    const coordinates = toLonLat(event.coordinate)
    const clickPoint: [number, number] = [coordinates[0], coordinates[1]]

    // Find the closest water segment
    let closestSegment = null
    let minDistance = Infinity

    waterSegmentsData.forEach((segment: any) => {
      if (segment.geometry) {
        try {
          const geojson = JSON.parse(segment.geometry)
          const segmentCoords = geojson.coordinates

          if (segmentCoords && segmentCoords.length >= 2) {
            // Calculate distance from click point to segment line
            for (let i = 0; i < segmentCoords.length - 1; i++) {
              const start = segmentCoords[i]
              const end = segmentCoords[i + 1]

              const distance = calculateDistanceFromPointToLine(clickPoint, [start[0], start[1]], [end[0], end[1]])
              if (distance < minDistance) {
                minDistance = distance
                closestSegment = segment
              }
            }
          }
        } catch (error) {
          console.error('Error parsing segment geometry for distance calculation:', error)
        }
      }
    })

    // Punkt można postawić gdziekolwiek. Jeśli klik jest blisko odcinka (~50 m),
    // proponujemy powiązanie; w przeciwnym razie punkt jest samodzielny.
    setCreatingPointMode(false)
    const target = map.getTarget()
    if (target && typeof target === 'object' && 'style' in target) {
      (target as HTMLElement).style.cursor = ''
    }

    setSelectedSegmentForPoint(closestSegment && minDistance <= 0.00045 ? closestSegment : null)
    setNewPointCoordinates(clickPoint)
    setShowPointModal(true)
  }

  // Helper function to calculate distance from point to line segment
  const calculateDistanceFromPointToLine = (
    point: [number, number],
    lineStart: [number, number],
    lineEnd: [number, number]
  ): number => {
    const [px, py] = point
    const [x1, y1] = lineStart
    const [x2, y2] = lineEnd

    // Vector from line start to end
    const dx = x2 - x1
    const dy = y2 - y1

    // If line segment has zero length, return distance to start point
    if (dx === 0 && dy === 0) {
      return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2)
    }

    // Parameter t represents projection of point onto infinite line
    const t = ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)

    // Clamp t to [0, 1] to stay within segment
    const clampedT = Math.max(0, Math.min(1, t))

    // Find closest point on segment
    const closestX = x1 + clampedT * dx
    const closestY = y1 + clampedT * dy

    // Return distance from point to closest point on segment
    return Math.sqrt((px - closestX) ** 2 + (py - closestY) ** 2)
  }

  // Handle map clicks for editing - select segment to edit
  const handleMapClickForEditing = (event: any) => {
    if (!editingMode || !mapInstance.current) return

    const map = mapInstance.current
    const pixel = map.getEventPixel(event.originalEvent)

    // Check if clicked on a water segment
    const features = map.getFeaturesAtPixel(pixel, {
      layerFilter: (layer) => (layer as any).get('id') === 'water-segments'
    })

    if (features.length > 0) {
      const feature = features[0]
      const segment = feature.get('waterSegmentData')

      if (segment && segment.geometry) {
        try {
          const geojson = JSON.parse(segment.geometry)
          const coordinates = geojson.coordinates

          if (coordinates && coordinates.length >= 2) {
            // Load segment geometry into drawing mode
            const points: [number, number][] = coordinates.map((coord: [number, number]) => [coord[0], coord[1]])

            setEditingSegment(segment)
            setEditingMode(false)
            setDrawingMode(true)
            setDrawingPoints(points)
            setShowDrawingControls(true)

            // Change cursor back to crosshair
            const target = map.getTarget()
            if (target && typeof target === 'object' && 'style' in target) {
              (target as HTMLElement).style.cursor = 'crosshair'
            }

            alert(`Wybrano odcinek "${segment?.name || 'Bez nazwy'}" do edycji. Możesz teraz przesuwać punkty lub dodawać nowe.`)
          }
        } catch (error) {
          console.error('Error parsing segment geometry:', error)
          alert('Błąd podczas ładowania geometrii odcinka')
        }
      }
    } else {
      alert('Nie kliknięto na żaden odcinek wodociągu. Spróbuj ponownie.')
    }
  }

  // Handle map clicks for drawing
  const handleMapClickForDrawing = (event: any) => {
    if (!drawingMode) return

    const coordinates = toLonLat(event.coordinate)
    const newPoint: [number, number] = [coordinates[0], coordinates[1]]

    setDrawingPoints(prev => {
      const newPoints = [...prev, newPoint]

      // Show drawing controls after first point
      if (newPoints.length === 1 && !showDrawingControls) {
        setShowDrawingControls(true)
      }

      return newPoints
    })
  }

  const handleRemoveLastPoint = () => {
    if (drawingPoints.length > 0) {
      setDrawingPoints(prev => prev.slice(0, -1))
    }
  }

  const handleCancelDrawing = () => {
    setDrawingMode(false)
    setDrawingPoints([])
    setShowDrawingControls(false)

    // Reset cursor
    if (mapInstance.current) {
      const target = mapInstance.current.getTarget()
      if (target && typeof target === 'object' && 'style' in target) {
        (target as HTMLElement).style.cursor = ''
      }
    }
  }

  const handleConfirmSegment = async () => {
    if (drawingPoints.length < 2) {
      alert('Odcinek musi mieć co najmniej 2 punkty')
      return
    }

    // Fetch SUW and segment options for the modal (default to water)
    await fetchSuwOptions('water')
    await fetchSegmentOptions('water')

    // Open modal for segment properties
    setShowSegmentModal(true)
  }

  const handleSegmentCategoryChange = async (category: 'water' | 'sewer') => {
    // Refetch SUW and segment options for the new category
    await fetchSuwOptions(category)
    await fetchSegmentOptions(category)
  }

  const handleSaveSegment = async (properties: any) => {
    try {
      // Convert coordinates to GeoJSON
      const geoJson = {
        type: 'LineString',
        coordinates: drawingPoints
      }

      const segmentData = {
        client_id: 1, // TODO: Get from current client context
        name: properties.name,
        segment_type: properties.segmentType,
        geometry: JSON.stringify(geoJson),
        diameter_mm: properties.diameter_mm,
        material: properties.material,
        parent_segment_id: properties.parent_segment_id,
        [properties.segmentCategory === 'water' ? 'suw_id' : 'treatment_plant_id']: properties.suw_id
      }

      // Check if this is an edit or create operation
      const isEdit = editingSegment !== null
      const apiPrefix = properties.segmentCategory === 'water' ? 'water-segments' : 'sewer-segments'
      const method = isEdit ? 'PUT' : 'POST'
      const url = isEdit ? `/api/${apiPrefix}/${editingSegment.id}` : `/api/${apiPrefix}/`

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(isEdit ? segmentData : segmentData)
      })

      if (response.ok) {
        const result = await response.json()
        console.log('Segment saved:', result)

        // Close modal and reset drawing
        setShowSegmentModal(false)
        handleCancelDrawing()

        // Reset editing state
        setEditingSegment(null)
        setEditingMode(false)

        // Refresh segments if they're being displayed
        if (properties.segmentCategory === 'water' && showWaterSegments) {
          fetchWaterSegments()
        } else if (properties.segmentCategory === 'sewer' && showSewerSegments) {
          fetchSewerSegments()
        }

        alert(`Odcinek ${properties.segmentCategory === 'water' ? 'wodociągu' : 'kanalizacji'} został ${isEdit ? 'zaktualizowany' : 'utworzony'} pomyślnie!`)
      } else {
        const error = await response.json()
        alert(`Błąd podczas ${isEdit ? 'aktualizacji' : 'tworzenia'} odcinka: ${error.detail || 'Nieznany błąd'}`)
      }
    } catch (error) {
      console.error('Error saving segment:', error)
      alert('Wystąpił błąd podczas zapisywania odcinka')
    }
  }

  const fetchSuwOptions = async (segmentCategory: 'water' | 'sewer' = 'water') => {
    try {
      const response = await fetch('/api/infrastructure/', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()

        if (segmentCategory === 'water') {
          // Filter for SUW (water treatment plants)
          const suws = data.filter((item: any) =>
            item.infrastructure_type?.toLowerCase().includes('stacja') &&
            item.infrastructure_type?.toLowerCase().includes('uzdatniania') &&
            item.infrastructure_type?.toLowerCase().includes('wody')
          )
          setSuwOptions(suws)
        } else {
          // Filter for treatment plants (wastewater treatment plants)
          const treatmentPlants = data.filter((item: any) =>
            item.infrastructure_type?.toLowerCase().includes('oczyszczalnia') &&
            item.infrastructure_type?.toLowerCase().includes('ścieków')
          )
          setSuwOptions(treatmentPlants)
        }
      }
    } catch (error) {
      console.error('Error fetching infrastructure options:', error)
    }
  }

  const fetchSegmentOptions = async (segmentCategory: 'water' | 'sewer' = 'water') => {
    try {
      const apiEndpoint = segmentCategory === 'water' ? '/api/water-segments/' : '/api/sewer-segments/'
      const response = await fetch(apiEndpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setSegmentOptions(data)
      }
    } catch (error) {
      console.error('Error fetching segment options:', error)
    }
  }

  // === Persist layer states to localStorage ===
  useEffect(() => { saveLayerState('infrastructure', showInfrastructure) }, [showInfrastructure])
  useEffect(() => { saveLayerState('dzialki', showDzialki) }, [showDzialki])
  useEffect(() => { saveLayerState('rejestr_wlasnosci', showRejestrWlasnosci) }, [showRejestrWlasnosci])
  useEffect(() => { saveLayerState('gaz', showGaz) }, [showGaz])
  useEffect(() => { saveLayerState('teletechnika', showTeletechnika) }, [showTeletechnika])
  useEffect(() => { saveLayerState('elektryka', showElektryka) }, [showElektryka])
  useEffect(() => { saveLayerState('flows', showFlows) }, [showFlows])

  // Odświeżanie danych punktów co 60 s, gdy którakolwiek z warstw korzystających
  // z /live jest włączona ("Przepływy" lub "Punkty pomiarowe"). Aktualizuje tylko
  // dane, bez przeładowania mapy.
  useEffect(() => {
    if (!showFlows && !showMeasurementPoints) return
    fetchFlows()
    const interval = setInterval(fetchFlows, 60000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFlows, showMeasurementPoints, token])
  useEffect(() => { saveLayerState('budynki', showBudynki) }, [showBudynki])
  useEffect(() => { saveLayerState('wodociagi', showWodociagi) }, [showWodociagi])
  useEffect(() => { saveLayerState('kanalizacja', showKanalizacja) }, [showKanalizacja])
  useEffect(() => { saveLayerState('granice_miejscowosci', showGraniceMiejscowosci) }, [showGraniceMiejscowosci])
  useEffect(() => { saveLayerState('water_segments', showWaterSegments) }, [showWaterSegments])
  useEffect(() => { saveLayerState('sewer_segments', showSewerSegments) }, [showSewerSegments])
  useEffect(() => { saveLayerState('realizations', showRealizations) }, [showRealizations])
  useEffect(() => { saveLayerState('network_valves', showNetworkValves) }, [showNetworkValves])
  useEffect(() => { saveLayerState('network_hydrants', showNetworkHydrants) }, [showNetworkHydrants])
  useEffect(() => {
    orthoLayerRef.current?.setVisible(showOrtofoto)
    saveLayerState('ortofoto', showOrtofoto)
  }, [showOrtofoto])
  useEffect(() => { saveLayerState('measurement_points', showMeasurementPoints) }, [showMeasurementPoints])
  useEffect(() => { saveLayerState('user_location', showUserLocation) }, [showUserLocation])

  // Śledzenie lokalizacji: włączamy/wyłączamy zależnie od warstwy "moja lokalizacja".
  // Odświeżanie co 30 s aktualizuje tylko marker - mapa nie jest przeładowywana.
  useEffect(() => {
    if (showUserLocation) {
      deviceLocation.startTracking()
    } else {
      deviceLocation.stopTracking()
    }
  }, [showUserLocation, deviceLocation.startTracking, deviceLocation.stopTracking])

  // === Cache fetched data to sessionStorage ===
  useEffect(() => { if (infrastructureData.length > 0) saveCachedData('infrastructure', infrastructureData) }, [infrastructureData])
  useEffect(() => { if (waterSegmentsData.length > 0) saveCachedData('water_segments', waterSegmentsData) }, [waterSegmentsData])
  useEffect(() => { if (sewerSegmentsData.length > 0) saveCachedData('sewer_segments', sewerSegmentsData) }, [sewerSegmentsData])
  useEffect(() => { if (realizationsData.length > 0) saveCachedData('realizations', realizationsData) }, [realizationsData])
  useEffect(() => { if (networkValvesData.length > 0) saveCachedData('network_valves', networkValvesData) }, [networkValvesData])
  useEffect(() => { if (networkHydrantsData.length > 0) saveCachedData('network_hydrants', networkHydrantsData) }, [networkHydrantsData])

  // === Auto-fetch data on mount when layers were restored from localStorage ===
  useEffect(() => {
    if (showInfrastructure && infrastructureData.length === 0) fetchInfrastructure()
    if (showWaterSegments && waterSegmentsData.length === 0) fetchWaterSegments()
    if (showSewerSegments && sewerSegmentsData.length === 0) fetchSewerSegments()
    if (showRealizations && realizationsData.length === 0) fetchRealizations()
    if (showNetworkValves && networkValvesData.length === 0) fetchNetworkValves()
    if (showNetworkHydrants && networkHydrantsData.length === 0) fetchNetworkHydrants()
  }, []) // Only run once on mount

  // === Network Valves layer ===
  useEffect(() => {
    if (!mapInstance.current) return
    const map = mapInstance.current
    const layers = map.getLayers().getArray()

    // Remove existing layer
    const existingLayer = layers.find(l => (l as any).get('id') === 'network-valves')
    if (existingLayer) map.removeLayer(existingLayer)

    if (showNetworkValves && networkValvesData.length > 0) {
      const vectorSource = new VectorSource()

      const tooltipEl = document.createElement('div')
      tooltipEl.className = 'bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-2 text-sm max-w-xs z-[2000]'
      tooltipEl.style.display = 'none'
      tooltipEl.style.pointerEvents = 'none'
      const tooltipOverlay = new Overlay({ element: tooltipEl, positioning: 'bottom-center', stopEvent: false, offset: [0, -10] })
      map.addOverlay(tooltipOverlay)

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: (feature) => {
          const valve = feature.get('valveData')
          const isOpen = valve.status === 'open'
          const color = isOpen ? '#16A34A' : '#DC2626' // Green open, Red closed
          return new Style({
            image: new Icon({
              src: 'data:image/svg+xml;base64,' + btoa(`
                <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="14" cy="14" r="12" fill="${color}" stroke="white" stroke-width="2"/>
                  <text x="14" y="18" text-anchor="middle" font-size="14" font-weight="bold" fill="white" font-family="Arial">Z</text>
                </svg>
              `),
              scale: 1,
              anchor: [0.5, 0.5]
            })
          })
        }
      })
      ;(vectorLayer as any).set('id', 'network-valves')

      networkValvesData.forEach((valve: any) => {
        const lat = parseFloat(valve.latitude)
        const lng = parseFloat(valve.longitude)
        if (!isNaN(lat) && !isNaN(lng)) {
          const feature = new Feature({
            geometry: new Point(fromLonLat([lng, lat])),
            valveData: valve
          })
          vectorSource.addFeature(feature)
        }
      })

      // Hover tooltip
      let currentFeature: Feature | null = null
      map.on('pointermove', (event) => {
        const pixel = map.getEventPixel(event.originalEvent)
        const hit = map.hasFeatureAtPixel(pixel, { layerFilter: (l) => (l as any).get('id') === 'network-valves' })
        if (hit) {
          const features = map.getFeaturesAtPixel(pixel, { layerFilter: (l) => (l as any).get('id') === 'network-valves' })
          if (features.length > 0) {
            const feature = features[0] as Feature
            const valve = feature.get('valveData')
            if (currentFeature !== feature && valve) {
              currentFeature = feature
              const statusPl = valve.status === 'open' ? '🟢 Otwarta' : '🔴 Zamknięta'
              tooltipEl.innerHTML = `
                <div class="font-semibold text-gray-900 mb-1">${valve.name}</div>
                <div class="text-gray-600 mb-1">Stan: ${statusPl}</div>
                ${valve.description ? `<div class="text-gray-500 mb-1">${valve.description}</div>` : ''}
                ${valve.last_changed_by_name ? `<div class="text-gray-400 text-xs">Zmienił: ${valve.last_changed_by_name}</div>` : ''}
                <div class="text-blue-500 text-xs mt-1 font-medium">Kliknij aby zmienić stan</div>
              `
              tooltipEl.style.display = 'block'
              tooltipOverlay.setPosition(event.coordinate)
            }
          }
        } else {
          if (currentFeature) { currentFeature = null; tooltipEl.style.display = 'none' }
        }
      })

      // Direct click handler for valves using 'click' event (more reliable than singleclick)
      const valveClickHandler = (event: any) => {
        const pixel = map.getEventPixel(event.originalEvent)
        let clickedValve: any = null
        map.forEachFeatureAtPixel(pixel, (feature) => {
          const valve = feature.get('valveData')
          if (valve && !clickedValve) {
            clickedValve = valve
          }
          return valve ? true : false
        }, { hitTolerance: 15 })
        
        if (clickedValve) {
          handleToggleValveStatusRef.current(clickedValve)
        }
      }
      map.on('click', valveClickHandler)

      map.addLayer(vectorLayer)

      return () => {
        map.un('click', valveClickHandler)
      }
    }
  }, [showNetworkValves, networkValvesData])

  // === Network Hydrants layer ===
  useEffect(() => {
    if (!mapInstance.current) return
    const map = mapInstance.current
    const layers = map.getLayers().getArray()

    const existingLayer = layers.find(l => (l as any).get('id') === 'network-hydrants')
    if (existingLayer) map.removeLayer(existingLayer)

    if (showNetworkHydrants && networkHydrantsData.length > 0) {
      const vectorSource = new VectorSource()

      const tooltipEl = document.createElement('div')
      tooltipEl.className = 'bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-2 text-sm max-w-xs z-[2000]'
      tooltipEl.style.display = 'none'
      tooltipEl.style.pointerEvents = 'none'
      const tooltipOverlay = new Overlay({ element: tooltipEl, positioning: 'bottom-center', stopEvent: false, offset: [0, -10] })
      map.addOverlay(tooltipOverlay)

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: (feature) => {
          const h = feature.get('hydrantData')
          const flushing = !!h.is_flushing_point
          // Kolor wypełnienia = wynik kontroli (brak danych => niesprawdzony)
          const status = h.inspection_status
          const fill = status === 'passed' ? '#16A34A'   // zielony = pozytywny
                     : status === 'failed' ? '#DC2626'   // czerwony = negatywny
                     : '#9CA3AF'                          // szary = niesprawdzony / brak danych
          // Obwódka = punkt płukania (niebieska, grubsza) vs zwykły (biała)
          const ring = flushing ? '#2563EB' : '#FFFFFF'
          const ringW = flushing ? 4 : 2
          return new Style({
            image: new Icon({
              src: 'data:image/svg+xml;base64,' + btoa(`
                <svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="15" cy="15" r="12" fill="${fill}" stroke="${ring}" stroke-width="${ringW}"/>
                  <text x="15" y="20" text-anchor="middle" font-size="14" font-weight="bold" fill="white" font-family="Arial">H</text>
                </svg>
              `),
              scale: 1,
              anchor: [0.5, 0.5]
            })
          })
        }
      })
      ;(vectorLayer as any).set('id', 'network-hydrants')

      networkHydrantsData.forEach((hydrant: any) => {
        const lat = parseFloat(hydrant.latitude)
        const lng = parseFloat(hydrant.longitude)
        if (!isNaN(lat) && !isNaN(lng)) {
          const feature = new Feature({
            geometry: new Point(fromLonLat([lng, lat])),
            hydrantData: hydrant
          })
          vectorSource.addFeature(feature)
        }
      })

      let currentFeature: Feature | null = null
      map.on('pointermove', (event) => {
        const pixel = map.getEventPixel(event.originalEvent)
        const hit = map.hasFeatureAtPixel(pixel, { layerFilter: (l) => (l as any).get('id') === 'network-hydrants' })
        if (hit) {
          const features = map.getFeaturesAtPixel(pixel, { layerFilter: (l) => (l as any).get('id') === 'network-hydrants' })
          if (features.length > 0) {
            const feature = features[0] as Feature
            const h = feature.get('hydrantData')
            if (currentFeature !== feature && h) {
              currentFeature = feature
              const meas: string[] = []
              if (h.pressure_dynamic != null) meas.push(`P.dyn ${h.pressure_dynamic} bar`)
              if (h.flow_rate != null) meas.push(`Q ${h.flow_rate} l/s`)
              if (h.pressure_static != null) meas.push(`P.stat ${h.pressure_static} bar`)
              const statusLine = h.inspection_status === 'passed'
                ? '<div class="text-green-700 mb-1">✅ Kontrola: pozytywna</div>'
                : h.inspection_status === 'failed'
                ? '<div class="text-red-700 mb-1">⛔ Kontrola: negatywna</div>'
                : '<div class="text-gray-500 mb-1">⚪ Niesprawdzony</div>'
              tooltipEl.innerHTML = `
                <div class="font-semibold text-gray-900 mb-1">🚒 ${h.name}${h.diameter ? ` <span class="text-gray-400 font-normal">${h.diameter}</span>` : ''}</div>
                ${statusLine}
                ${h.is_flushing_point ? '<div class="text-blue-600 mb-1">💦 Punkt płukania sieci</div>' : ''}
                ${h.address ? `<div class="text-gray-600 mb-1">${h.address}</div>` : ''}
                ${h.measurement_date ? `<div class="text-gray-500">📅 Kontrola: ${h.measurement_date}${h.coefficient_k != null ? ` • K=${h.coefficient_k}` : ''}</div>` : ''}
                ${meas.length ? `<div class="text-gray-700">${meas.join(' • ')}</div>` : ''}
                ${h.inspection_note ? `<div class="text-amber-700 mb-1">📝 ${h.inspection_note}</div>` : ''}
                <div class="text-blue-500 text-xs mt-1 font-medium">Kliknij aby zobaczyć/edytować</div>
              `
              tooltipEl.style.display = 'block'
              tooltipOverlay.setPosition(event.coordinate)
            }
          }
        } else {
          if (currentFeature) { currentFeature = null; tooltipEl.style.display = 'none' }
        }
      })

      const hydrantClickHandler = (event: any) => {
        const pixel = map.getEventPixel(event.originalEvent)
        let clickedHydrant: any = null
        map.forEachFeatureAtPixel(pixel, (feature) => {
          const h = feature.get('hydrantData')
          if (h && !clickedHydrant) {
            clickedHydrant = h
          }
          return h ? true : false
        }, { hitTolerance: 15 })

        if (clickedHydrant) {
          handleHydrantClickRef.current(clickedHydrant)
        }
      }
      map.on('click', hydrantClickHandler)

      map.addLayer(vectorLayer)

      return () => {
        map.un('click', hydrantClickHandler)
      }
    }
  }, [showNetworkHydrants, networkHydrantsData])

  // === Warstwa "Przepływy" (punkty pomiarowe z ostatnią wartością) ===
  useEffect(() => {
    if (!mapInstance.current) return
    const map = mapInstance.current
    const layers = map.getLayers().getArray()

    const existingLayer = layers.find(l => (l as any).get('id') === 'flows')
    if (existingLayer) map.removeLayer(existingLayer)

    if (showFlows && flowsData.length > 0) {
      const vectorSource = new VectorSource()

      const tooltipEl = document.createElement('div')
      tooltipEl.className = 'bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-2 text-sm max-w-xs z-[2000]'
      tooltipEl.style.display = 'none'
      tooltipEl.style.pointerEvents = 'none'
      const tooltipOverlay = new Overlay({ element: tooltipEl, positioning: 'bottom-center', stopEvent: false, offset: [0, -14] })
      map.addOverlay(tooltipOverlay)

      const colorForFlow = (flow: number | null, stale: boolean): string => {
        if (stale || flow === null) return '#9ca3af'   // szary - brak świeżych danych
        if (flow <= 0.1) return '#93c5fd'
        if (flow < 5) return '#3b82f6'
        if (flow < 20) return '#2563eb'
        return '#1d4ed8'
      }

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: (feature) => {
          const p = feature.get('flowData')
          const stale = !!p.stale
          const alarm = !!p.has_alarms
          const flow = (p.flow !== null && p.flow !== undefined) ? Number(p.flow) : null
          const color = colorForFlow(flow, stale)
          const ring = alarm ? '#dc2626' : 'white'   // czerwona obwódka przy alarmie
          const ringW = alarm ? 4 : 3
          const label = stale || flow === null ? '—' : flow.toFixed(1)
          return new Style({
            image: new Icon({
              src: 'data:image/svg+xml;base64,' + btoa(`
                <svg width="46" height="46" viewBox="0 0 46 46" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="23" cy="23" r="15" fill="${color}" stroke="${ring}" stroke-width="${ringW}"/>
                  <text x="23" y="27" text-anchor="middle" font-size="12" font-weight="bold" fill="white" font-family="Arial">${label}</text>
                </svg>
              `),
              scale: 1,
              anchor: [0.5, 0.5]
            })
          })
        }
      })
      ;(vectorLayer as any).set('id', 'flows')

      flowsData.forEach((p: any) => {
        const lat = parseFloat(p.latitude)
        const lng = parseFloat(p.longitude)
        if (!isNaN(lat) && !isNaN(lng)) {
          const feature = new Feature({ geometry: new Point(fromLonLat([lng, lat])), flowData: p })
          vectorSource.addFeature(feature)
        }
      })

      let currentFeature: Feature | null = null
      map.on('pointermove', (event) => {
        const pixel = map.getEventPixel(event.originalEvent)
        const hit = map.hasFeatureAtPixel(pixel, { layerFilter: (l) => (l as any).get('id') === 'flows' })
        if (hit) {
          const features = map.getFeaturesAtPixel(pixel, { layerFilter: (l) => (l as any).get('id') === 'flows' })
          if (features.length > 0) {
            const feature = features[0] as Feature
            const p = feature.get('flowData')
            if (currentFeature !== feature && p) {
              currentFeature = feature
              const flowTxt = (p.flow !== null && p.flow !== undefined) ? `${Number(p.flow).toFixed(1)} m³/h` : 'brak'
              const meterTxt = (p.meter !== null && p.meter !== undefined) ? `${Number(p.meter).toFixed(1)} m³` : 'brak'
              const when = p.last_reading_at ? new Date(p.last_reading_at).toLocaleString('pl-PL') : '—'
              tooltipEl.innerHTML = `
                <div class="font-semibold text-gray-900 mb-1">📊 ${p.name}</div>
                ${p.stale ? '<div class="text-amber-600 mb-1">⚠ Brak świeżych danych</div>' : ''}
                <div class="text-gray-700">Przepływ: <span class="font-medium">${flowTxt}</span></div>
                <div class="text-gray-700">Wodomierz: <span class="font-medium">${meterTxt}</span></div>
                <div class="text-gray-400 text-xs mt-1">Odczyt: ${when}</div>
              `
              tooltipEl.style.display = 'block'
              tooltipOverlay.setPosition(event.coordinate)
            }
          }
        } else {
          if (currentFeature) { currentFeature = null; tooltipEl.style.display = 'none' }
        }
      })

      const flowClickHandler = (event: any) => {
        const pixel = map.getEventPixel(event.originalEvent)
        let clicked: any = null
        map.forEachFeatureAtPixel(pixel, (feature) => {
          const p = feature.get('flowData')
          if (p && !clicked) clicked = p
          return p ? true : false
        }, { hitTolerance: 12 })
        if (clicked) handleFlowClickRef.current(clicked)
      }
      map.on('click', flowClickHandler)

      map.addLayer(vectorLayer)

      return () => {
        map.un('click', flowClickHandler)
      }
    }
  }, [showFlows, flowsData])

  // Klik w punkt przepływu otwiera historię (pomijamy w trakcie przenoszenia punktu)
  handleFlowClickRef.current = (p: any) => {
    if (movingPointRef.current) return
    setHistoryPoint({ id: p.id, name: p.name })
  }

  // === Przenoszenie / edycja / usuwanie punktu pomiarowego ===
  const handleStartMovingPoint = (point: { id: number; name: string }) => {
    setHistoryPoint(null)
    setEditPoint(null)
    movingPointRef.current = point
    setMovingPoint(point)
    if (mapInstance.current) {
      const target = mapInstance.current.getTarget()
      if (target && typeof target === 'object' && 'style' in target) {
        (target as HTMLElement).style.cursor = 'crosshair'
      }
    }
    showInfo('Przenoszenie punktu', `Kliknij na mapie nowe położenie punktu „${point.name}".`)
  }

  const cancelMovingPoint = () => {
    movingPointRef.current = null
    setMovingPoint(null)
    if (mapInstance.current) {
      const target = mapInstance.current.getTarget()
      if (target && typeof target === 'object' && 'style' in target) {
        (target as HTMLElement).style.cursor = ''
      }
    }
  }

  const handleMapClickForPointMove = async (event: any) => {
    const point = movingPointRef.current
    if (!point || !token) { cancelMovingPoint(); return }
    const coordinates = toLonLat(event.coordinate)
    try {
      const res = await fetch(`/api/measurement-points/${point.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          latitude: coordinates[1].toString(),
          longitude: coordinates[0].toString(),
        }),
      })
      if (res.ok) {
        showSuccess('Przeniesiono punkt', `Nowe położenie punktu „${point.name}" zostało zapisane.`)
        fetchFlows()
      } else {
        const err = await res.json().catch(() => ({}))
        showError('Błąd', err.detail || 'Nie udało się przenieść punktu.')
      }
    } catch {
      showError('Błąd', 'Wystąpił błąd podczas przenoszenia punktu.')
    } finally {
      cancelMovingPoint()
    }
  }

  const handleStartEditPoint = async (pointRef: { id: number; name: string }) => {
    if (!token) return
    try {
      const res = await fetch(`/api/measurement-points/${pointRef.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (res.ok) {
        setHistoryPoint(null)
        setEditPoint(await res.json())
      } else {
        showError('Błąd', 'Nie udało się pobrać danych punktu.')
      }
    } catch {
      showError('Błąd', 'Nie udało się pobrać danych punktu.')
    }
  }

  // === Warstwa "Punkty pomiarowe" (statyczne lokalizacje, widoczne nawet bez danych) ===
  // Niezależna od telemetrii: pokazuje WSZYSTKIE punkty pomiarowe jako piny lokalizacji.
  // Dzięki temu świeżo dodany punkt jest widoczny od razu, zanim spłyną jakiekolwiek odczyty.
  useEffect(() => {
    if (!mapInstance.current) return
    const map = mapInstance.current
    const layers = map.getLayers().getArray()

    const existingLayer = layers.find(l => (l as any).get('id') === 'measurement-points')
    if (existingLayer) map.removeLayer(existingLayer)

    if (showMeasurementPoints && flowsData.length > 0) {
      const vectorSource = new VectorSource()

      const tooltipEl = document.createElement('div')
      tooltipEl.className = 'bg-white border border-gray-300 rounded-lg shadow-lg px-3 py-2 text-sm max-w-xs z-[2000]'
      tooltipEl.style.display = 'none'
      tooltipEl.style.pointerEvents = 'none'
      const tooltipOverlay = new Overlay({ element: tooltipEl, positioning: 'bottom-center', stopEvent: false, offset: [0, -30] })
      map.addOverlay(tooltipOverlay)

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: (feature) => {
          const p = feature.get('pointData')
          const alarm = !!p.has_alarms
          const ring = alarm ? '#dc2626' : '#ffffff'
          return new Style({
            image: new Icon({
              // Pin lokalizacji z mini-wykresem słupkowym - wyraźnie inny od okrągłej
              // "plakietki" przepływu. Same znaki ASCII, więc btoa działa bez konwersji.
              src: 'data:image/svg+xml;base64,' + btoa(`
                <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 39C15 39 27 24 27 14A12 12 0 1 0 3 14C3 24 15 39 15 39Z" fill="#0891b2" stroke="${ring}" stroke-width="2"/>
                  <circle cx="15" cy="14" r="7" fill="white"/>
                  <rect x="10" y="14" width="2.4" height="4" fill="#0891b2"/>
                  <rect x="13.8" y="11" width="2.4" height="7" fill="#0891b2"/>
                  <rect x="17.6" y="12.5" width="2.4" height="5.5" fill="#0891b2"/>
                </svg>
              `),
              scale: 1,
              anchor: [0.5, 1]
            })
          })
        }
      })
      ;(vectorLayer as any).set('id', 'measurement-points')

      flowsData.forEach((p: any) => {
        const lat = parseFloat(p.latitude)
        const lng = parseFloat(p.longitude)
        if (!isNaN(lat) && !isNaN(lng)) {
          const feature = new Feature({ geometry: new Point(fromLonLat([lng, lat])), pointData: p })
          vectorSource.addFeature(feature)
        }
      })

      let currentFeature: Feature | null = null
      const pointMoveHandler = (event: any) => {
        const pixel = map.getEventPixel(event.originalEvent)
        const hit = map.hasFeatureAtPixel(pixel, { layerFilter: (l) => (l as any).get('id') === 'measurement-points' })
        if (hit) {
          const features = map.getFeaturesAtPixel(pixel, { layerFilter: (l) => (l as any).get('id') === 'measurement-points' })
          if (features.length > 0) {
            const feature = features[0] as Feature
            const p = feature.get('pointData')
            if (currentFeature !== feature && p) {
              currentFeature = feature
              const flowTxt = (p.flow !== null && p.flow !== undefined) ? `${Number(p.flow).toFixed(1)} m³/h` : 'brak danych'
              tooltipEl.innerHTML = `
                <div class="font-semibold text-gray-900 mb-1">📊 ${p.name}</div>
                <div class="text-gray-700">Przepływ: <span class="font-medium">${flowTxt}</span></div>
                ${p.stale ? '<div class="text-amber-600 text-xs mt-1">⚠ Brak świeżych danych</div>' : ''}
                <div class="text-blue-500 text-xs mt-1 font-medium">Kliknij, aby zobaczyć historię</div>
              `
              tooltipEl.style.display = 'block'
              tooltipOverlay.setPosition(event.coordinate)
            }
          }
        } else {
          if (currentFeature) { currentFeature = null; tooltipEl.style.display = 'none' }
        }
      }
      map.on('pointermove', pointMoveHandler)

      const pointClickHandler = (event: any) => {
        const pixel = map.getEventPixel(event.originalEvent)
        let clicked: any = null
        map.forEachFeatureAtPixel(pixel, (feature) => {
          const p = feature.get('pointData')
          if (p && !clicked) clicked = p
          return p ? true : false
        }, { hitTolerance: 12 })
        if (clicked) handleFlowClickRef.current(clicked)
      }
      map.on('click', pointClickHandler)

      map.addLayer(vectorLayer)

      return () => {
        map.un('pointermove', pointMoveHandler)
        map.un('click', pointClickHandler)
        map.removeOverlay(tooltipOverlay)
      }
    }
  }, [showMeasurementPoints, flowsData])

  // Update drawing layer when drawing points change
  useEffect(() => {
    if (!mapInstance.current) return

    const map = mapInstance.current
    const layers = map.getLayers().getArray()

    // Remove existing drawing layer
    const existingDrawingLayer = layers.find(layer => (layer as any).get('id') === 'drawing')
    if (existingDrawingLayer) {
      map.removeLayer(existingDrawingLayer)
    }

    if (drawingMode && drawingPoints.length > 0) {
      const vectorSource = new VectorSource()

      // Add points
      drawingPoints.forEach((point, index) => {
        const feature = new Feature({
          geometry: new Point(fromLonLat(point)),
          pointIndex: index,
          isDrawingPoint: true
        })
        vectorSource.addFeature(feature)
      })

      // Add line if we have at least 2 points
      if (drawingPoints.length >= 2) {
        const coordinates = drawingPoints.map(point => fromLonLat(point))
        const lineFeature = new Feature({
          geometry: new LineString(coordinates),
          isDrawingLine: true
        })
        vectorSource.addFeature(lineFeature)
      }

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: (feature) => {
          const isPoint = feature.get('isDrawingPoint')
          const isLine = feature.get('isDrawingLine')

          if (isPoint) {
            return new Style({
              image: new Icon({
                src: 'data:image/svg+xml;base64,' + btoa(`
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="6" cy="6" r="5" fill="#FF6B35" stroke="#FFFFFF" stroke-width="2"/>
                  </svg>
                `),
                scale: 1
              })
            })
          } else if (isLine) {
            return new Style({
              stroke: new Stroke({
                color: '#FF6B35',
                width: 3,
                lineDash: [5, 5] // Dashed line to show it's being drawn
              })
            })
          }
        }
      })

      ;(vectorLayer as any).set('id', 'drawing')
      map.addLayer(vectorLayer)
      setDrawingLayer(vectorLayer)
    }
  }, [drawingMode, drawingPoints])

  // Update measurement layer when measurement points change
  useEffect(() => {
    if (!mapInstance.current) return

    const map = mapInstance.current
    const layers = map.getLayers().getArray()
    const existingLayer = layers.find(layer => (layer as any).get('id') === 'measurement-drawing')
    if (existingLayer) {
      map.removeLayer(existingLayer)
    }

    if (measurementPoints.length > 0) {
      const vectorSource = new VectorSource()

      measurementPoints.forEach((point, index) => {
        const feature = new Feature({
          geometry: new Point(fromLonLat(point)),
          pointIndex: index,
          isMeasurementPoint: true
        })
        vectorSource.addFeature(feature)
      })

      if (measurementPoints.length >= 2) {
        const coordinates = measurementPoints.map(point => fromLonLat(point))
        const lineFeature = new Feature({
          geometry: new LineString(coordinates),
          isMeasurementLine: true
        })
        vectorSource.addFeature(lineFeature)
      }

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: (feature) => {
          if (feature.get('isMeasurementPoint')) {
            return new Style({
              image: new Icon({
                src: 'data:image/svg+xml;base64,' + btoa(`
                  <svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="7" cy="7" r="6" fill="#0EA5E9" stroke="#FFFFFF" stroke-width="2"/>
                  </svg>
                `),
                scale: 1
              })
            })
          }

          if (feature.get('isMeasurementLine')) {
            return new Style({
              stroke: new Stroke({
                color: '#0EA5E9',
                width: 3,
                lineDash: [8, 5]
              })
            })
          }

        }
      })

      ;(vectorLayer as any).set('id', 'measurement-drawing')
      map.addLayer(vectorLayer)
    }
  }, [measurementPoints])

  // Add modify interaction for editing drawing points
  useEffect(() => {
    if (!mapInstance.current || !drawingLayer) return

    const map = mapInstance.current
    const interactions = map.getInteractions().getArray()

    // Remove existing modify interaction
    const existingModify = interactions.find(interaction => (interaction as any).get('id') === 'drawing-modify')
    if (existingModify) {
      map.removeInteraction(existingModify)
    }

    if (drawingMode && drawingPoints.length > 0) {
      // Create modify interaction for the drawing layer
      const modify = new Modify({
        source: drawingLayer.getSource(),
        style: new Style({
          image: new Icon({
            src: 'data:image/svg+xml;base64,' + btoa(`
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="7" fill="#FF6B35" stroke="#FFFFFF" stroke-width="2"/>
                <circle cx="8" cy="8" r="3" fill="#FFFFFF"/>
              </svg>
            `),
            scale: 1
          })
        })
      })

      ;(modify as any).set('id', 'drawing-modify')

      // Handle modify end event to update drawing points
      modify.on('modifyend', (event) => {
        const features = event.features.getArray()
        const updatedPoints: [number, number][] = []

        // Collect all point features and sort by pointIndex
        const pointFeatures = features
          .filter(feature => feature.get('isDrawingPoint'))
          .sort((a, b) => (a.get('pointIndex') || 0) - (b.get('pointIndex') || 0))

        pointFeatures.forEach(feature => {
          const geometry = feature.getGeometry()
          if (geometry instanceof Point) {
            const coords = toLonLat(geometry.getCoordinates())
            updatedPoints.push([coords[0], coords[1]])
          }
        })

        // Update drawing points if we have the same number of points
        if (updatedPoints.length === drawingPoints.length) {
          setDrawingPoints(updatedPoints)
        }
      })

      map.addInteraction(modify)
    }

    return () => {
      // Cleanup function
      const modifyInteraction = interactions.find(interaction => (interaction as any).get('id') === 'drawing-modify')
      if (modifyInteraction) {
        map.removeInteraction(modifyInteraction)
      }
    }
  }, [drawingMode, drawingPoints, drawingLayer])

  return (
    <MapLayout
      showInfrastructure={showInfrastructure}
      onToggleInfrastructure={handleToggleInfrastructure}
      showDzialki={showDzialki}
      onToggleDzialki={handleToggleDzialki}
      showBudynki={showBudynki}
      onToggleBudynki={handleToggleBudynki}
      showRejestrWlasnosci={showRejestrWlasnosci}
      onToggleRejestrWlasnosci={handleToggleRejestrWlasnosci}
      showGaz={showGaz}
      onToggleGaz={handleToggleGaz}
      showTeletechnika={showTeletechnika}
      onToggleTeletechnika={handleToggleTeletechnika}
      showElektryka={showElektryka}
      onToggleElektryka={handleToggleElektryka}
      featureInfoMode={featureInfoMode}
      onToggleFeatureInfo={handleToggleFeatureInfo}
      showWodociagi={showWodociagi}
      onToggleWodociagi={handleToggleWodociagi}
      showKanalizacja={showKanalizacja}
      onToggleKanalizacja={handleToggleKanalizacja}
      showGraniceMiejscowosci={showGraniceMiejscowosci}
      onToggleGraniceMiejscowosci={handleToggleGraniceMiejscowosci}
      editMode={editMode}
      onToggleEditMode={handleToggleEditMode}
      showWaterSegments={showWaterSegments}
      onToggleWaterSegments={handleToggleWaterSegments}
      showSewerSegments={showSewerSegments}
      onToggleSewerSegments={handleToggleSewerSegments}
      showUserLocation={showUserLocation}
      onToggleUserLocation={() => setShowUserLocation(!showUserLocation)}
      showMeasurementPoints={showMeasurementPoints}
      onToggleMeasurementPoints={() => setShowMeasurementPoints(!showMeasurementPoints)}
      showFlows={showFlows}
      onToggleFlows={handleToggleFlows}
      onManageControllers={() => setShowControllersModal(true)}
      showRealizations={showRealizations}
      onToggleRealizations={handleToggleRealizations}
      showNetworkValves={showNetworkValves}
      onToggleNetworkValves={handleToggleNetworkValves}
      onStartPlacingValve={handleStartPlacingValve}
      showNetworkHydrants={showNetworkHydrants}
      onToggleNetworkHydrants={handleToggleNetworkHydrants}
      showOrtofoto={showOrtofoto}
      onToggleOrtofoto={handleToggleOrtofoto}
      onStartPlacingHydrant={handleStartPlacingHydrant}
      onStartCreatingPoint={handleStartCreatingPoint}
      onImportHydrants={() => setShowHydrantImport(true)}
      onStartDrawingSegment={handleStartDrawingSegment}
      onStartEditingSegment={handleStartEditingSegment}
      showDrawingControls={showDrawingControls}
      onRemoveLastPoint={handleRemoveLastPoint}
      onCancelDrawing={handleCancelDrawing}
      onConfirmSegment={handleConfirmSegment}
    >
      <div className={`relative ${isFullscreen ? 'fixed inset-0 z-50' : 'h-full'}`}>
        {/* Odczyt dokładności lokalizacji (gdy warstwa "moja lokalizacja" aktywna) */}
        {showUserLocation && (
          <div className="absolute top-[4.75rem] left-1/2 -translate-x-1/2 z-[1001] pointer-events-none">
            <div className="flex items-center gap-2 bg-white/90 backdrop-blur border border-gray-200 rounded-full shadow px-3 py-1 text-xs">
              {deviceLocation.location ? (
                <>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: deviceLocation.location.accuracy <= 10 ? '#16a34a' : deviceLocation.location.accuracy <= 30 ? '#d97706' : '#dc2626' }} />
                  <span className="text-gray-700 font-medium">± {Math.round(deviceLocation.location.accuracy)} m</span>
                  {followMode && <span className="text-blue-600">• śledzenie</span>}
                  {deviceLocation.headingSource && <span className="text-gray-400">• {deviceLocation.headingSource === 'gps' ? 'kurs GPS' : 'kompas'}</span>}
                </>
              ) : deviceLocation.error ? (
                <span className="text-red-600">{deviceLocation.error}</span>
              ) : (
                <span className="text-gray-500">Pobieranie pozycji GPS…</span>
              )}
            </div>
          </div>
        )}
        {/* Top Toolbar */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1001]">
          <div className="bg-white/90 backdrop-blur border border-gray-200 rounded-2xl shadow-lg p-1.5 flex items-center gap-1.5">
            <button
              onClick={handleToggleMeasurementMode}
              className={`min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl transition-all active:scale-95 ${measurementMode ? 'bg-blue-600 text-white shadow-md' : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'}`}
              title="Pomiar odległości"
            >
              <span className="text-xl">📏</span>
            </button>
          </div>
        </div>

        {/* Baner trybu "informacje o obiekcie" */}
        {featureInfoMode && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[1002] w-[94%] max-w-md">
            <div className="bg-indigo-600/95 backdrop-blur text-white rounded-2xl shadow-xl px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 text-sm font-medium min-w-0">
                <span className="text-lg shrink-0">ℹ️</span>
                <span>Kliknij obiekt sieci, aby zobaczyć jego dane</span>
              </div>
              <button
                onClick={handleToggleFeatureInfo}
                className="shrink-0 min-h-[40px] px-4 rounded-xl bg-white/20 hover:bg-white/30 active:bg-white/40 text-sm font-medium transition-colors"
              >
                Zakończ
              </button>
            </div>
          </div>
        )}

        {/* Panel wyników GetFeatureInfo */}
        {featureInfoData && (
          <div className="absolute bottom-4 left-3 z-[1002] w-[92%] max-w-sm">
            <div className="bg-white/95 backdrop-blur border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-800">Informacje o obiekcie</h3>
                <button
                  onClick={() => setFeatureInfoData(null)}
                  className="p-1 -mr-1 text-gray-400 hover:text-gray-600 rounded-lg"
                  aria-label="Zamknij"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto px-4 py-3 text-sm">
                {featureInfoData.loading ? (
                  <div className="flex items-center gap-2 text-gray-500 py-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></span>
                    Pobieranie danych…
                  </div>
                ) : featureInfoData.error ? (
                  <p className="text-red-600 py-1">{featureInfoData.error}</p>
                ) : featureInfoData.html ? (
                  <div
                    className="feature-info-html text-xs text-gray-800 leading-snug break-words [&_table]:w-full [&_table]:border-collapse [&_table]:my-1 [&_td]:px-1.5 [&_td]:py-1 [&_td]:align-top [&_td]:border-b [&_td]:border-gray-100 [&_th]:px-1.5 [&_th]:py-1 [&_th]:text-left [&_th]:text-gray-500 [&_th]:font-semibold [&_b]:text-gray-900 [&_hr]:my-2 [&_hr]:border-gray-200"
                    dangerouslySetInnerHTML={{ __html: featureInfoData.html }}
                  />
                ) : (
                  <p className="text-gray-500 py-1">Brak obiektów sieci w tym punkcie. Kliknij dokładnie na linię lub symbol.</p>
                )}
                <div className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-400">
                  {featureInfoData.coord[1].toFixed(6)}, {featureInfoData.coord[0].toFixed(6)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Baner trybu dodawania punktu (zasuwa/hydrant) */}
        {(placingValveMode || placingHydrantMode) && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[1002] w-[94%] max-w-md">
            <div className="bg-blue-600/95 backdrop-blur text-white rounded-2xl shadow-xl px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 text-sm font-medium min-w-0">
                <span className="text-lg shrink-0">{placingHydrantMode ? '🚒' : '🔴'}</span>
                <span>Dotknij mapy, aby umieścić {placingHydrantMode ? 'hydrant' : 'zasuwę'}</span>
              </div>
              <button
                onClick={cancelPlacementMode}
                className="shrink-0 min-h-[40px] px-4 rounded-xl bg-white/20 hover:bg-white/30 active:bg-white/40 text-sm font-medium transition-colors"
              >
                Anuluj
              </button>
            </div>
          </div>
        )}

        {/* Pływający pasek rysowania odcinka (kluczowy na mobile, gdzie sidebar jest schowany) */}
        {showDrawingControls && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[1002] w-[94%] max-w-md">
            <div className="bg-white/95 backdrop-blur border border-gray-200 rounded-2xl shadow-xl p-2 flex items-center gap-2">
              <button
                onClick={handleRemoveLastPoint}
                className="flex-1 min-h-[44px] px-2 rounded-xl text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 active:bg-orange-200 transition-colors"
              >
                ↩︎ Cofnij
              </button>
              <button
                onClick={handleCancelDrawing}
                className="flex-1 min-h-[44px] px-2 rounded-xl text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 active:bg-red-200 transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={handleConfirmSegment}
                className="flex-[1.4] min-h-[44px] px-2 rounded-xl text-sm font-semibold text-white bg-green-600 hover:bg-green-700 active:bg-green-800 shadow-sm transition-colors"
              >
                ✓ Zatwierdź
              </button>
            </div>
          </div>
        )}

        {(measurementMode || measurementPoints.length > 0) && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[1001] w-[92%] max-w-md">
            <div className="bg-white/95 backdrop-blur border border-gray-300 rounded-xl shadow-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-800">Pomiar odległości</h3>
                <button
                  onClick={handleClearMeasurement}
                  className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Czyść zaznaczone
                </button>
              </div>

              {measurementSegments.length === 0 ? (
                <p className="text-xs text-gray-600">Wskaż pierwszy i drugi punkt na mapie.</p>
              ) : (
                <div className="space-y-1 max-h-44 overflow-auto">
                  {measurementSegments.map(seg => (
                    <div key={seg.index} className="text-xs text-gray-700 flex justify-between">
                      <span>Odcinek {seg.index}</span>
                      <span className="font-medium">{formatDistance(seg.distance)}</span>
                    </div>
                  ))}
                  <div className="pt-2 mt-2 border-t border-gray-200 text-sm font-semibold text-gray-900 flex justify-between">
                    <span>Suma</span>
                    <span>{formatDistance(measurementTotal)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Map Controls */}
        <div className="absolute top-3 right-3 z-[1000] flex flex-col gap-2">
          {/* Zoom +/- */}
          <div className="bg-white/90 backdrop-blur border border-gray-200 rounded-2xl shadow-lg overflow-hidden flex flex-col divide-y divide-gray-100">
            <button
              onClick={() => zoomBy(1)}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
              title="Przybliż"
              aria-label="Przybliż"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
            <button
              onClick={() => zoomBy(-1)}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
              title="Oddal"
              aria-label="Oddal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
          </div>

          {/* Wyśrodkuj na mojej lokalizacji */}
          <button
            onClick={centerOnUserLocation}
            className={`min-w-[44px] min-h-[44px] flex items-center justify-center backdrop-blur border rounded-2xl shadow-lg transition-all active:scale-95 ${
              showUserLocation && deviceLocation.location
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'bg-white/90 border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
            title="Pokaż moją lokalizację"
            aria-label="Pokaż moją lokalizację"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="3" strokeWidth={2} />
              <path strokeLinecap="round" strokeWidth={2} d="M12 2v3m0 14v3M2 12h3m14 0h3" />
              <circle cx="12" cy="12" r="8" strokeWidth={2} />
            </svg>
          </button>

          <button
            onClick={toggleFullscreen}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center bg-white/90 backdrop-blur border border-gray-200 rounded-2xl shadow-lg text-gray-700 hover:bg-gray-50 active:scale-95 transition-all"
            title={isFullscreen ? 'Zamknij pełny ekran' : 'Pełny ekran'}
          >
            {isFullscreen ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.5 3.5M15 9h4.5M15 9V4.5M15 9l5.5-5.5M9 15v4.5M9 15H4.5M9 15l-5.5 5.5M15 15h4.5M15 15v4.5m0-4.5l5.5 5.5" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 3l-6 6m0 0V4m0 5h5M3 21l6-6m0 0v5m0-5H4" />
              </svg>
            )}
          </button>

          <button
            onClick={resetView}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center bg-white/90 backdrop-blur border border-gray-200 rounded-2xl shadow-lg text-gray-700 hover:bg-gray-50 active:scale-95 transition-all"
            title="Resetuj widok"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12a9 9 0 0118 0c0 4.97-4.03 9-9 9s-9-4.03-9-9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a3 3 0 006 0" />
            </svg>
          </button>
        </div>

        {/* Zoom Display - Bottom Right */}
        <div className="absolute bottom-4 right-3 z-[1000] bg-white/80 backdrop-blur rounded-full shadow px-3 py-1.5 pointer-events-none">
          <div className="text-xs font-medium text-gray-600">
            Zoom: {mapZoom.toFixed(1)}
          </div>
        </div>

        {/* Map Container */}
        <div
          ref={mapRef}
          className="w-full h-full"
          style={{ position: 'relative' }}
        />

        {/* Loading overlay */}
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center pointer-events-none opacity-0 transition-opacity">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-gray-600">Ładowanie mapy...</p>
          </div>
        </div>
      </div>

      {/* Segment Properties Modal */}
      <SegmentPropertiesModal
        isOpen={showSegmentModal}
        onClose={() => setShowSegmentModal(false)}
        onSave={handleSaveSegment}
        coordinates={drawingPoints}
        suwOptions={suwOptions}
        segmentOptions={segmentOptions}
        clientId={1} // TODO: Get from current client context
        onSegmentCategoryChange={handleSegmentCategoryChange}
      />

      {/* Measurement Point Modal */}
      <MeasurementPointModal
        isOpen={showPointModal}
        onClose={() => {
          setShowPointModal(false)
          setSelectedSegmentForPoint(null)
          setNewPointCoordinates(null)
        }}
        onSave={() => {
          setShowPointModal(false)
          setSelectedSegmentForPoint(null)
          setNewPointCoordinates(null)
          // Pokaż nowy punkt od razu na warstwie "Punkty pomiarowe" (widoczna bez danych)
          if (!showMeasurementPoints) setShowMeasurementPoints(true)
          fetchFlows()
        }}
        coordinates={newPointCoordinates || [0, 0]}
        targetSegment={selectedSegmentForPoint}
      />

      {/* Controllers Modal */}
      <ControllersModal
        isOpen={showControllersModal}
        token={token}
        onClose={() => setShowControllersModal(false)}
      />

      {/* Measurement History Modal */}
      <MeasurementHistoryModal
        isOpen={!!historyPoint}
        point={historyPoint}
        token={token}
        canManage={editMode}
        onClose={() => setHistoryPoint(null)}
        onChanged={fetchFlows}
        onMove={() => historyPoint && handleStartMovingPoint(historyPoint)}
        onEdit={() => historyPoint && handleStartEditPoint(historyPoint)}
        onDeleted={() => { setHistoryPoint(null); fetchFlows() }}
      />

      {/* Measurement Point Edit Modal */}
      <MeasurementPointModal
        isOpen={!!editPoint}
        editPoint={editPoint}
        onClose={() => setEditPoint(null)}
        onSave={() => { setEditPoint(null); fetchFlows() }}
        coordinates={editPoint ? [parseFloat(editPoint.longitude) || 0, parseFloat(editPoint.latitude) || 0] : [0, 0]}
        targetSegment={null}
      />

      {/* Pasek trybu przenoszenia punktu */}
      {movingPoint && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9998] flex items-center gap-3 bg-cyan-700 text-white px-4 py-3 rounded-xl shadow-2xl">
          <span className="text-sm">📍 Kliknij nowe położenie punktu „{movingPoint.name}"</span>
          <button
            onClick={cancelMovingPoint}
            className="min-h-[36px] px-3 rounded-lg text-sm font-medium bg-white/20 hover:bg-white/30"
          >
            Anuluj
          </button>
        </div>
      )}

      {/* Valve Creation Modal */}
      <ValveCreationModal
        isOpen={showValveModal}
        onClose={() => {
          setShowValveModal(false)
          setNewValveCoordinates(null)
        }}
        onSave={handleSaveValve}
        coordinates={newValveCoordinates}
      />

      {/* Valve Status Change Modal */}
      <ValveStatusChangeModal
        isOpen={!!valveForStatusChange}
        onClose={() => setValveForStatusChange(null)}
        onConfirm={handleValveStatusConfirm}
        valve={valveForStatusChange}
      />

      {/* Hydrant Creation Modal */}
      <HydrantCreationModal
        isOpen={showHydrantModal}
        onClose={() => {
          setShowHydrantModal(false)
          setNewHydrantCoordinates(null)
        }}
        onSave={handleSaveHydrant}
        coordinates={newHydrantCoordinates}
      />

      {/* Hydrant Edit Modal */}
      <HydrantEditModal
        isOpen={!!hydrantForEdit}
        hydrant={hydrantForEdit}
        onClose={() => setHydrantForEdit(null)}
        onSave={handleUpdateHydrant}
        onDelete={handleDeleteHydrant}
      />

      {/* Hydrant Import Modal */}
      <HydrantImportModal
        isOpen={showHydrantImport}
        token={token}
        clientId={mapClientId}
        onClose={() => setShowHydrantImport(false)}
        onImported={() => {
          if (!showNetworkHydrants) setShowNetworkHydrants(true)
          fetchNetworkHydrants()
        }}
      />

      {/* User Location Marker */}
      <UserLocationMarker
        map={mapInstance.current}
        location={deviceLocation.location}
        heading={deviceLocation.heading}
        isVisible={showUserLocation && !!deviceLocation.location}
      />
    </MapLayout>
  )
}
